import { Component, ViewEncapsulation, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CertificateService } from '../../services/certificate.service';
import { SignService } from '../../services/sign.service';
import { RseSignService } from '../../services/rse-sign.service';
import { CertificateVerificationService } from '../../services/verify.service';
import { Certificate } from '../../models/certificate.model';
import { SignPdfAppearanceEnabled } from '../../models/sign-pdf-api.model';
import { SignMultipartResult } from '../../models/sign-multipart-api.model';
import {
  SignRseAppearance,
  SignRseProcessResponse,
  SignRseSignatureType
} from '../../models/sign-rse-api.model';
import {
  buildRseAuthPreparingHtml,
  normalizeCadesOutputExtension,
  resolveRseSignedFileName
} from '../../core/utils/sign-rse.utils';
import {
  RseAuthModalComponent,
  RseAuthModalData,
  RseAuthModalResult
} from '../rse-auth-modal/rse-auth-modal.component';
import { VerifySignatureResponse, CertificateDetail, VerificationResult, VerifyDocumentResultItem } from '../../models/verify-signature-response.model';
import { AuthModalComponent } from '../auth-modal/auth-modal.component';
import Swal from 'sweetalert2';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { UploadP12ModalComponent } from '../upload-p12-modal/upload-p12-modal.component';
import { SignedFilesModalComponent, SignedFilesModalData } from '../signed-files-modal/signed-files-modal.component';
import { TokenFisicoCertificateModalComponent, TokenFisicoCertificateModalData } from '../token-fisico-certificate-modal/token-fisico-certificate-modal.component';
import {
  PadesSignatureZoneModalComponent,
  PadesSignatureZoneResult,
  PadesSignatureZoneModalResult,
  PadesSignatureImageChoice,
  PadesVisibleSignatureConfig
} from '../pades-signature-zone-modal/pades-signature-zone-modal.component';
import { UiHelperService } from '../../services/ui-helper.service';
import { CertificateInfoModalComponent } from '../certificate-info-modal/certificate-info-modal.component';
import { SignatureValidationModalComponent, SignatureValidationModalData } from '../signature-validation-modal/signature-validation-modal.component';
import { Subject, of, from, throwError } from 'rxjs';
import { filter, takeUntil, map, catchError, concatMap, toArray, finalize } from 'rxjs/operators';
import { FILE_SIZE, FILE_LIMITS, ALLOWED_FILE_TYPES, FILE_ERROR_MESSAGES, FILE_EXTENSIONS, maxSignFileSizeBytes, maxSignFileSizeMb, fileTooLargeMessage } from '../../core/constants';
import { dnCommonName, parseDnAttributes } from '../../core/utils/cert-subject.utils';
import { SignStateService, SignState } from '../../core/services/sign-state.service';
import { VerifyStateService, VerifyState } from '../../core/services/verify-state.service';
import { TabStateService } from '../../core/services/tab-state.service';
import { SignMechanismService, SignMechanism } from '../../core/services/sign-mechanism.service';
import { LocalAgentService, LocalAgentPdfDocument, LocalAgentCertificate } from '../../core/services/local-agent.service';
import { AgentAvailabilityService } from '../../core/services/agent-availability.service';
import { NotificationService } from '../../services/notification.service';
import { environment } from '../../../environments/environment';
import { TokenFisicoAlertModalComponent, TokenFisicoAlertModalData, TokenFisicoAlertType } from '../token-fisico-alert-modal/token-fisico-alert-modal.component';
import { buildAdobeStampDescription, buildAdobeStyleStampImageBase64 } from '../../core/utils/pades-stamp-image.util';

/** Imagen 1x1 transparente en base64 (PNG). Se envía cuando el backend exige imageBase64 y no se usa imagen (ej. sello por defecto). */
const TRANSPARENT_1X1_PNG_BASE64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

type TabType = 'sign' | 'verify';
type FileType = 'pdf' | 'other';

/** Configuración de firma visible por PDF (un elemento por archivo). */
type PdfVisibleSignatureEntry = {
  zone: PadesSignatureZoneResult;
  imageChoice: PadesSignatureImageChoice;
  visibleSignature: PadesVisibleSignatureConfig;
};
type CertificateType = 'p10' | 'token';
type ViewMode = 'all' | 'sign-only' | 'verify-only' | 'reports-only';

@Component({
    selector: 'app-main-workspace',
    templateUrl: './main-workspace.component.html',
    styleUrls: ['./main-workspace.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MainWorkspaceComponent implements OnDestroy {
  @ViewChild('pdfFileInput') pdfFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('otherFileInput') otherFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('signFilesStep') signFilesStep?: ElementRef<HTMLElement>;

  // Subject para cleanup de suscripciones (previene memory leaks)
  private destroy$ = new Subject<void>();
  
  isLoading = false;
  activeTab: TabType = 'sign';
  
  // Modo de visualización basado en la ruta
  viewMode: ViewMode = 'all';

  /** Tarjeta PDF/Otro sobre la que se arrastra un archivo (feedback visual). */
  signDropTarget: FileType | null = null;

  // Sign section state
  signFileType: FileType = 'pdf';
  signFiles: File[] = [];
  signCertificateType: CertificateType = 'token';
  pdfBase64s: string[] = [];
  cadesBase64s: string[] = [];
  signedFileSrcs: string[] = [];
  isSigned = false;
  showPdfViewer = false;
  currentViewerIndex: number = 0;

  // Certificate related state
  certificates: Certificate[] = [];
  selectedCertificate: Certificate | null = null;
  uploadedCertificate: string | null = null;
  uploadedCertificatePassword: string | null = null;
  user = '';
  password = '';

  // Verify section state
  verifyFile: File | null = null;
  verificationResult: VerificationResult | null = null;
  certificateList: CertificateDetail[] = [];
  selectedDetail: CertificateDetail | null = null;
  selectedRowIndex: number | null = null;
  /** Panel lateral "Firmas" (solo PDF); visible con ítems colapsados */
  verifyPanelOpen = true;
  /** Menú contextual clic derecho en zona de firma */
  verifyContextMenuVisible = false;
  verifyContextMenuX = 0;
  verifyContextMenuY = 0;
  verifyContextMenuCert: CertificateDetail | null = null;
  verifyContextMenuIndex = -1;
  /** URL del archivo a verificar para el visor PDF (se revoca al resetear) */
  verifyFileObjectUrl: string | null = null;
  /** Fecha/hora de la última verificación (para mostrar "Última comprobación" tipo Adobe) */
  verifyLastCheckDate: Date | null = null;


  /** Si estamos dentro del shell (rutas /firmar o /verificar): ocultar selector de certificado y usar mecanismo del sidebar */
  isShellLayout = false;

  /** Certifirma Digital Físico: null = no comprobado, true = agente disponible, false = no disponible */
  agentAvailable: boolean | null = null;
  /** Certifirma Digital Físico: true mientras se comprueba si el agente está disponible */
  agentChecking = false;

  /**
   * Firma visible por PDF (índice alineado con signFiles).
   * El usuario marca qué archivos llevan sello/imagen y define zona en cada uno.
   */
  pdfWantVisibleSignature: boolean[] = [];
  pdfVisibleSignatureConfigs: Array<PdfVisibleSignatureEntry | null> = [];
  private pdfVisibleFileKeys: string[] = [];
  /**
   * Estampa de tiempo (TSA) para el lote de firma actual.
   * MVP: UI comentada; siempre false hasta reactivar en otra rama.
   */
  signTimestampEnabled = false;
  /**
   * LTV (solo PAdES; requiere TSA).
   * MVP: UI comentada; siempre false hasta reactivar en otra rama.
   */
  signLtvEnabled = false;
  /** CN del firmante para firma visible (Certifirma Digital Virtual o P10), obtenido al autenticarse. */
  visibleSignerCn: string | null = null;
  /** Propósito del modal de auth/carga P10: firmar de inmediato o preparar firma visible. */
  private authPurpose: 'sign' | 'firmaVisible' = 'sign';
  /** PDF pendiente de abrir en el modal de zona tras autenticarse. */
  private pendingFirmaVisibleFileIndex: number | null = null;
  /**
   * Popup Entrust abierto en el clic de «Continuar con la firma» (gesto de usuario)
   * para que el navegador no lo bloquee tras el create async del proceso RSE.
   */
  private pendingRseAuthPopup: Window | null = null;
  /** Modal RSE abierto al clic (preparing) para transición continua sin spinner. */
  private rseActiveAuthDialog: import('@angular/material/dialog').MatDialogRef<
    RseAuthModalComponent,
    RseAuthModalResult
  > | null = null;
  private readonly visibleSignatureFallbackText = 'Firmante';
  private readonly visibleSignaturePlaceholders = new Set([
    'firmante',
    'nombre del firmante',
    'nombre del firmante.'
  ]);
  private auditTransactionId: string | null = null;
  private auditSerial: string | null = null;
  private auditIdentificationNumber: string | null = null;

  constructor(
    private certificateService: CertificateService,
    private signService: SignService,
    private rseSignService: RseSignService,
    private verifyService: CertificateVerificationService,
    public dialog: MatDialog,
    public router: Router,
    private route: ActivatedRoute,
    private uiHelper: UiHelperService,
    public signStateService: SignStateService,
    public verifyStateService: VerifyStateService,
    private tabStateService: TabStateService,
    public signMechanismService: SignMechanismService,
    private localAgentService: LocalAgentService,
    private agentAvailability: AgentAvailabilityService,
    private notificationService: NotificationService
  ) {
    this.detectViewMode();
    this.updateShellLayoutFromUrl(this.router.url);
    // La primera NavigationEnd puede haber ocurrido antes de suscribirnos: al refrescar /verificar
    // el tab quedaba en "firmar" (default del servicio). Alinear tab con la URL antes del primer render.
    if (this.isShellLayout) {
      this.syncTabFromUrl(this.router.url);
    }
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(e => {
        const url = e.urlAfterRedirects || e.url;
        this.updateShellLayoutFromUrl(url);
        this.syncTabFromUrl(url);
      });

    // Suscribirse al tab activo desde el servicio
    // Cuando el tab cambia, limpiar los formularios de la sección opuesta
    this.tabStateService.activeTab
      .pipe(takeUntil(this.destroy$))
      .subscribe(tab => {
        const previousTab = this.activeTab;
        this.activeTab = tab;
        
        // Solo limpiar si realmente cambió de tab
        if (previousTab !== tab) {
          this.clearFormsOnTabChange(tab);
        }
      });
    
    // Suscribirse a cambios de estado para mantener propiedades locales sincronizadas
    // (para compatibilidad con código existente que usa propiedades directas)
    this.signStateService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.syncSignStateFromService(state);
      });

    this.signMechanismService.value$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mechanism => {
        // Evitar que un CN placeholder de RSE contamine TV / P10 / Certifirma Digital Físico
        this.resetVisibleSignatureState();
        this.dropSignFilesExceedingLimit(mechanism);
        if (this.isShellLayout && mechanism === 'token-fisico') {
          this.syncAgentAvailabilityForTokenFisico();
        } else {
          this.agentAvailable = null;
          this.agentChecking = false;
        }
      });

    // Mantener la vista alineada con el health del shell (Fase 1).
    this.agentAvailability.isAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ok) => {
        if (!this.isTokenFisicoActive) {
          return;
        }
        this.agentAvailable = ok;
        this.agentChecking = false;
      });

    this.verifyStateService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.syncVerifyStateFromService(state);
        
        // Si hay un archivo nuevo y autoVerify está activo, ejecutar verificación automáticamente (ej. desde "Verificar" en modal de firmados)
        if (state.file && state.autoVerify && !state.isProcessing && !state.isVerified) {
          this.verifyStateService.resetAutoVerify();
          setTimeout(() => this.verifySignature(), 80);
        }
      });
  }

  // Getters para observables del estado (para usar con async pipe en template)
  get signState$() {
    return this.signStateService.state$;
  }

  get verifyState$() {
    return this.verifyStateService.state$;
  }

  /**
   * Detecta el modo de visualización desde route data (solo para /certitool)
   */
  private detectViewMode(): void {
    // Obtener el modo desde route data (solo para /certitool)
    const routeData = this.route.snapshot.data;
    const mode = routeData['mode'] as ViewMode;
    
    // Si no hay modo definido, usar 'sign-only' por defecto
    this.viewMode = mode || 'sign-only';
    
    // Inicializar el tab activo según el modo
    this.initializeActiveTab();
    
    // Suscribirse a cambios de route data para actualizar el modo dinámicamente
    this.route.data
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        const currentMode = data['mode'] as ViewMode;
        if (currentMode && currentMode !== this.viewMode) {
          this.viewMode = currentMode || 'sign-only';
          this.initializeActiveTab();
        }
      });
  }

  /**
   * Inicializa el tab activo según el modo de visualización
   */
  private initializeActiveTab(): void {
    switch (this.viewMode) {
      case 'sign-only':
        this.activeTab = 'sign';
        break;
      case 'verify-only':
        this.activeTab = 'verify';
        break;
      case 'reports-only':
        // Cuando se implemente reportes, inicializar aquí
        // this.activeTab = 'reports';
        break;
      case 'all':
      default:
        // Mantener el tab actual o usar 'sign' por defecto
        if (!this.activeTab) {
          this.activeTab = 'sign';
        }
        break;
    }
  }

  /**
   * Indica si se debe mostrar el sidebar con las tabs
   */
  get showSidebar(): boolean {
    return false; // Sidebar oculto, navegación ahora en el header
    return this.viewMode === 'all';
  }

  /**
   * Retorna las tabs disponibles según el modo de visualización
   */
  get availableTabs(): TabType[] {
    switch (this.viewMode) {
      case 'sign-only':
        return ['sign'];
      case 'verify-only':
        return ['verify'];
      case 'reports-only':
        // Cuando se implemente reportes, agregar aquí
        return [];
      case 'all':
      default:
        return ['sign', 'verify'];
    }
  }

  /**
   * Sincroniza las propiedades locales con el estado del servicio
   * (para compatibilidad con código existente)
   */
  private syncSignStateFromService(state: SignState): void {
    this.signFileType = state.fileType;
    this.signFiles = state.files;
    this.syncPdfVisibleSignatureArrays();
    this.signCertificateType = state.certificateType;
    this.pdfBase64s = state.pdfBase64s;
    this.cadesBase64s = state.cadesBase64s;
    this.signedFileSrcs = state.signedFiles;
    this.isSigned = state.isSigned;
    this.showPdfViewer = state.showPdfViewer;
    this.currentViewerIndex = state.currentViewerIndex;
    this.certificates = state.certificates;
    this.selectedCertificate = state.selectedCertificate;
    this.uploadedCertificate = state.uploadedCertificate;
    this.uploadedCertificatePassword = state.uploadedCertificatePassword;
    this.user = state.user;
    this.password = state.password;
  }

  /**
   * Sincroniza las propiedades locales con el estado del servicio de verificación
   */
  private syncVerifyStateFromService(state: VerifyState): void {
    this.verifyFile = state.file;
    this.verificationResult = state.verificationResult;
    this.certificateList = state.signatures;
    this.selectedDetail = state.selectedSignature;
    this.selectedRowIndex = state.selectedIndex;
    // Gestionar URL del visor PDF: crear cuando hay resultado y archivo, revocar cuando no
    if (!state.verificationResult || !state.file) {
      if (this.verifyFileObjectUrl) {
        URL.revokeObjectURL(this.verifyFileObjectUrl);
        this.verifyFileObjectUrl = null;
      }
    } else if (state.file && !this.verifyFileObjectUrl) {
      this.verifyFileObjectUrl = URL.createObjectURL(state.file);
    }
  }

  /** Indica si el archivo a verificar es PDF (para mostrar visor) */
  get isVerifyFilePdf(): boolean {
    const name = this.verifyFile?.name?.toLowerCase() ?? '';
    return name.endsWith(FILE_EXTENSIONS.PDF);
  }

  /** Indica si el archivo a verificar es XML (XAdES Enveloped). */
  get isVerifyFileXml(): boolean {
    const name = this.verifyFile?.name?.toLowerCase() ?? '';
    return name.endsWith(FILE_EXTENSIONS.XML);
  }

  /** Etiqueta corta del formato en barra/visor de verificación. */
  get verifyFileFormatLabel(): string {
    if (this.isVerifyFilePdf) return 'PDF';
    if (this.isVerifyFileXml) return 'XAdES';
    return 'CAdES';
  }

  /** Texto de éxito en la barra de resultado según formato del archivo. */
  get verifySuccessBarText(): string {
    if (this.isVerifyFilePdf) {
      return 'Firmado. Todas las firmas son válidas.';
    }
    const plural = this.certificateList.length > 1;
    if (this.isVerifyFileXml) {
      return plural
        ? 'Documento XAdES válido. Firmas verificadas.'
        : 'Documento XAdES válido. Firma verificada.';
    }
    return plural
      ? 'Contenedor CAdES válido. Firmas verificadas.'
      : 'Contenedor CAdES válido. Firma verificada.';
  }


  setActiveTab(tab: TabType): void {
    // Actualizar el tab en el servicio (esto notificará a todos los suscriptores)
    // La limpieza se ejecutará automáticamente en la suscripción
    this.tabStateService.setActiveTab(tab);
  }

  /**
   * Limpia los formularios cuando se cambia de tab
   */
  private clearFormsOnTabChange(tab: TabType): void {
    if (tab === 'sign') {
      // Limpiar la sección de verificación completamente
      this.verifyStateService.reset();
      // Limpiar la sección de firma (por si había datos previos)
      this.signStateService.resetSignSection();
    } else {
      // Limpiar la sección de firma completamente
      this.resetVisibleSignatureState();
      this.signStateService.reset();
      // Limpiar la sección de verificación (por si había datos previos)
      this.verifyStateService.resetResults();
    }
  }

  /** Actualiza isShellLayout según la URL (layout Propuesta 5B) */
  private updateShellLayoutFromUrl(url: string): void {
    this.isShellLayout = url.includes('firmar') || url.includes('verificar');
  }

  /** Sincroniza el tab activo con la URL cuando estamos en el shell */
  private syncTabFromUrl(url: string): void {
    if (!this.isShellLayout) return;
    if (url.includes('verificar')) {
      this.tabStateService.setActiveTab('verify');
    } else if (url.includes('firmar')) {
      this.tabStateService.setActiveTab('sign');
    }
  }

  /** Mecanismo actual desde el sidebar (solo relevante cuando isShellLayout) */
  get signMechanism(): SignMechanism {
    return this.signMechanismService.current;
  }

  /** Etiqueta del mecanismo de firma actual para el título del área de trabajo (visible cuando el menú está oculto en móvil) */
  get signMechanismLabel(): string {
    switch (this.signMechanism) {
      case 'rse': return 'Certifirma Digital Virtual con OTP';
      case 'token-virtual': return 'Certifirma Digital Virtual';
      case 'p10': return 'Certifirma Digital PKCS10';
      case 'token-fisico': return 'Certifirma Digital Físico';
      default: return 'Certifirma Digital Virtual con OTP';
    }
  }

  /** Descripción contextual por mecanismo (qué es y para qué sirve). */
  get signMechanismDescription(): string {
    switch (this.signMechanism) {
      case 'token-virtual':
        return 'Sube tus archivos y fírmalos digitalmente con tu Certifirma Digital Virtual.';
      case 'p10':
        return 'Sube tus archivos y fírmalos con tu Certifirma Digital PKCS10 (.p10/.pfx).';
      case 'token-fisico':
        return 'Firma con la llave USB de tu Certifirma Digital Físico mediante el complemento local.';
      case 'rse':
        return 'Valida tu identidad con el código de seguridad (OTP) enviado a tu correo y firma tus documentos.';
      default:
        return 'Sube tus archivos y fírmalos digitalmente con tu Certifirma.';
    }
  }

  get signMaxFileSizeMb(): number {
    return maxSignFileSizeMb(this.signMechanism);
  }

  get signFileLimitHint(): string {
    return `Hasta ${FILE_LIMITS.MAX_FILES} archivos · máx. ${this.signMaxFileSizeMb}MB c/u`;
  }

  /** Certifirma Digital Físico activo (shell + mecanismo Certifirma Digital Físico) */
  get isTokenFisicoActive(): boolean {
    return this.isShellLayout && this.signMechanism === 'token-fisico';
  }

  /** Mostrar solo la vista "complemento no detectado" (no los pasos de firma) */
  get showTokenFisicoNotAvailableView(): boolean {
    return this.isTokenFisicoActive && (this.agentChecking || this.agentAvailable === false);
  }

  /** Mostrar flujo normal de firma para Certifirma Digital Físico (agente detectado) */
  get showTokenFisicoSignFlow(): boolean {
    return this.isTokenFisicoActive && this.agentAvailable === true;
  }

  /** Deshabilitar botón principal de firma cuando Certifirma Digital Físico no tiene agente */
  get isTokenFisicoPlaceholder(): boolean {
    return this.isTokenFisicoActive && this.agentAvailable !== true;
  }

  /** Habilitar botón Firmar (Certifirma Digital Físico): basta tener archivos (firma visible es opcional) */
  get canTokenFisicoSign(): boolean {
    return this.signFiles.length > 0;
  }

  /** Marcó firma visible pero falta definir zona en algún PDF */
  hasPdfVisiblePendingConfiguration(): boolean {
    return this.pdfWantVisibleSignature.some(
      (w, i) => w && !this.pdfVisibleSignatureConfigs[i]
    );
  }

  /** Deshabilita Autenticarse / Firmar / Cargar P10 si falta zona de firma visible */
  get isSignPrimaryActionDisabled(): boolean {
    if (this.isTokenFisicoPlaceholder) {
      return true;
    }
    if (this.showTokenFisicoSignFlow) {
      if (!this.canTokenFisicoSign) {
        return true;
      }
      if (this.signFileType === 'pdf' && this.hasPdfVisiblePendingConfiguration()) {
        return true;
      }
      return false;
    }
    if (this.signFiles.length === 0) {
      return true;
    }
    if (this.signFileType === 'pdf' && this.hasPdfVisiblePendingConfiguration()) {
      return true;
    }
    return false;
  }

  /** Credenciales listas para firmar sin volver a autenticar. */
  get isSignCredentialsReady(): boolean {
    const state = this.signStateService.currentState;
    if (this.signCertificateType === 'token') {
      return !!(state.selectedCertificate && state.user && state.password);
    }
    return !!(state.uploadedCertificate && state.uploadedCertificatePassword);
  }

  get signPrimaryActionLabel(): string {
    if (this.isTokenFisicoPlaceholder) {
      return 'Complemento no detectado';
    }
    if (this.signMechanism === 'rse') {
      return 'Continuar a firmar';
    }
    if (this.showTokenFisicoSignFlow) {
      return 'Firmar';
    }
    if (this.isSignCredentialsReady) {
      return 'Firmar';
    }
    return this.signCertificateType === 'token' ? 'Continuar a firmar' : 'Elegir Certifirma Digital PKCS10';
  }

  get signPrimaryActionAriaLabel(): string {
    if (this.isTokenFisicoPlaceholder) {
      return 'Certifirma Digital Físico: complemento no detectado';
    }
    if (this.showTokenFisicoSignFlow) {
      return 'Firmar documentos con Certifirma Digital Físico';
    }
    if (this.signMechanism === 'rse') {
      return 'Continuar a firmar e iniciar la autenticación en Certicámara';
    }
    if (this.isSignCredentialsReady) {
      return 'Firmar documentos con el certificado ya autenticado';
    }
    return this.signCertificateType === 'token'
      ? 'Continuar a firmar con Certifirma Digital Virtual'
      : 'Elegir archivo Certifirma Digital PKCS10 para firmar documentos';
  }

  /**
   * Paso visual del timeline de firma (1=Cargar, 2=Revisar, 3=Firmar).
   * No altera el flujo; solo guía la UI.
   */
  get signFlowStep(): 1 | 2 | 3 {
    if (this.signFiles.length === 0) {
      return 1;
    }
    if (this.isSignPrimaryActionDisabled) {
      return 2;
    }
    return 3;
  }

  setPdfWantVisibleSignature(index: number, want: boolean): void {
    if (index < 0 || index >= this.pdfWantVisibleSignature.length) return;
    this.pdfWantVisibleSignature[index] = want;
    if (!want) {
      this.pdfVisibleSignatureConfigs[index] = null;
    }
  }

  isPdfVisibleReady(index: number): boolean {
    return !!(
      this.pdfWantVisibleSignature[index] && this.pdfVisibleSignatureConfigs[index]
    );
  }

  private pdfVisibleFileKey(f: File): string {
    return `${f.name}|${f.size}`;
  }

  /** Conserva preferencias al reordenar / quitar archivos (clave nombre+tamaño). */
  private syncPdfVisibleSignatureArrays(): void {
    if (this.signFileType !== 'pdf') {
      this.pdfWantVisibleSignature = [];
      this.pdfVisibleSignatureConfigs = [];
      this.pdfVisibleFileKeys = [];
      return;
    }
    const files = this.signFiles;
    const prevMap = new Map<
      string,
      { want: boolean; cfg: PdfVisibleSignatureEntry | null }
    >();
    for (let i = 0; i < this.pdfVisibleFileKeys.length; i++) {
      prevMap.set(this.pdfVisibleFileKeys[i], {
        want: !!this.pdfWantVisibleSignature[i],
        cfg: this.pdfVisibleSignatureConfigs[i] ?? null
      });
    }
    this.pdfWantVisibleSignature = files.map((f) => {
      const prev = prevMap.get(this.pdfVisibleFileKey(f));
      return prev ? prev.want : true;
    });
    this.pdfVisibleSignatureConfigs = files.map((f) => {
      const prev = prevMap.get(this.pdfVisibleFileKey(f));
      return prev ? prev.cfg : null;
    });
    this.pdfVisibleFileKeys = files.map((f) => this.pdfVisibleFileKey(f));
  }

  /**
   * Usa el snapshot del shell si ya hay health; si no, fuerza un recheck.
   * El botón "Reintentar" del panel también llama aquí.
   */
  checkAgentAvailability(): void {
    this.syncAgentAvailabilityForTokenFisico(true);
  }

  private syncAgentAvailabilityForTokenFisico(forceRefresh = false): void {
    if (!forceRefresh && this.agentAvailability.snapshot) {
      this.agentAvailable = true;
      this.agentChecking = false;
      return;
    }
    this.agentChecking = true;
    if (!forceRefresh) {
      this.agentAvailable = null;
    }
    this.agentAvailability
      .refreshOnce()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (available) => {
          this.agentAvailable = available;
          this.agentChecking = false;
        },
        error: () => {
          this.agentAvailable = false;
          this.agentChecking = false;
        }
      });
  }

  get agentDownloadUrl(): string {
    return environment.agentDownloadUrl ?? '';
  }

  get agentReleaseLabel(): string {
    return `${environment.agentDisplayName} — ${environment.agentReleaseLabel}`;
  }

  /** Abre la URL de descarga del complemento o copia la ruta local al portapapeles. */
  openAgentDownload(): void {
    const url = environment.agentDownloadUrl;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Ruta local: el navegador no permite abrir file://. Copiar al portapapeles y avisar.
    const path = url;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(path).then(() => {
        Swal.fire({
          icon: 'info',
          title: 'Ruta copiada',
          html:
            `La ruta del complemento se ha copiado al portapapeles.<br><br>` +
            `<strong>${this.agentReleaseLabel}</strong><br><br>` +
            'Ábrela en el Explorador de archivos y ejecuta el instalador.',
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
      }).catch(() => {
        this.showAgentLocalPathFallback(path);
      });
    } else {
      this.showAgentLocalPathFallback(path);
    }
  }

  private showAgentLocalPathFallback(path: string): void {
    Swal.fire({
      icon: 'info',
      title: 'Complemento local',
      html:
        `<strong>${this.agentReleaseLabel}</strong><br><br>` +
        'Ejecuta el instalador desde esta ruta en tu equipo:<br><br><code style="word-break:break-all;font-size:0.85em;">' +
        path +
        '</code>',
      confirmButtonText: 'Entendido',
      ...this.notificationService.getSwalBaseConfig()
    });
  }

  // --- Sign Section Logic ---
  selectSignFileType(type: FileType): void {
    if (type === 'other') {
      this.resetVisibleSignatureState();
    }
    this.signStateService.setFileType(type);
  }

  selectSignCertificateType(type: CertificateType): void {
    this.signStateService.setCertificateType(type);
  }

  /**
   * Clic en tarjeta PDF / Otro archivo: fija el tipo y abre el explorador.
   * Si ya hay archivos del mismo tipo, abre el picker para agregar más (sin limpiar).
   */
  onFileTypeCardClick(type: FileType): void {
    if (this.signFileType !== type) {
      this.selectSignFileType(type);
    }
    this.openSignFilePicker();
  }

  /** Abre el input file del tipo de firma actual. */
  openSignFilePicker(): void {
    if (this.signFiles.length >= FILE_LIMITS.MAX_FILES) {
      Swal.fire({
        icon: 'error',
        title: 'Demasiados archivos',
        text: `Máximo ${FILE_LIMITS.MAX_FILES} archivos permitidos.`,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }
    const input =
      this.signFileType === 'pdf'
        ? this.pdfFileInput?.nativeElement
        : this.otherFileInput?.nativeElement;
    input?.click();
  }

  onFileTypeDragOver(event: DragEvent, type: FileType): void {
    event.preventDefault();
    event.stopPropagation();
    this.signDropTarget = type;
  }

  onFileTypeDragLeave(event: DragEvent, type: FileType): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.signDropTarget === type) {
      this.signDropTarget = null;
    }
  }

  /** Drop sobre tarjeta PDF / Otro: fija tipo y procesa archivos. */
  onFileTypeDrop(event: DragEvent, type: FileType): void {
    event.preventDefault();
    event.stopPropagation();
    this.signDropTarget = null;

    if (!event.dataTransfer?.files || event.dataTransfer.files.length === 0) {
      return;
    }

    if (this.signFileType !== type) {
      this.selectSignFileType(type);
    }

    this.processNewSignFiles(Array.from(event.dataTransfer.files));
  }

  onSignFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.processNewSignFiles(Array.from(input.files));
    input.value = '';
  }

  onSignDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.signDropTarget = null;

    if (!event.dataTransfer?.files || event.dataTransfer.files.length === 0) {
      return;
    }

    this.processNewSignFiles(Array.from(event.dataTransfer.files));
  }

  /** Si se cambia de mecanismo, quita archivos que superen el nuevo tope. */
  private dropSignFilesExceedingLimit(mechanism: SignMechanism): void {
    const maxBytes = maxSignFileSizeBytes(mechanism);
    const files = this.signStateService.currentState.files || [];
    const oversizedIndexes = files
      .map((file, index) => (file.size > maxBytes ? index : -1))
      .filter(index => index >= 0);
    if (oversizedIndexes.length === 0) {
      return;
    }
    for (let i = oversizedIndexes.length - 1; i >= 0; i--) {
      this.signStateService.removeFile(oversizedIndexes[i]);
    }
    const remaining = this.signStateService.currentState.files || [];
    if (remaining.length === 0) {
      this.signStateService.resetSignSection();
      this.resetSignFileInputs();
    }
    Swal.fire({
      icon: 'warning',
      title: 'Archivos fuera de límite',
      text: `Este mecanismo admite máximo ${maxSignFileSizeMb(mechanism)}MB por archivo. Se quitaron ${oversizedIndexes.length} archivo(s) que superaban ese tamaño.`,
      confirmButtonText: 'Entendido',
      ...this.notificationService.getSwalBaseConfig()
    });
  }

  /**
   * Valida y agrega archivos al estado de firma (selección o drag & drop).
   * Conserva las mismas reglas que el flujo anterior.
   */
  private processNewSignFiles(newFiles: File[]): void {
    const state = this.signStateService.currentState;
    const existingFiles = state.files || [];
    const hadNoFiles = existingFiles.length === 0;

    const maxBytes = maxSignFileSizeBytes(this.signMechanism);
    const invalidSizeFiles = newFiles.filter(file => file.size > maxBytes);
    if (invalidSizeFiles.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo demasiado grande',
        text: fileTooLargeMessage(maxSignFileSizeMb(this.signMechanism)),
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    const emptyFiles = newFiles.filter(file => file.size < FILE_LIMITS.MIN_FILE_SIZE);
    if (emptyFiles.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo inválido',
        text: FILE_ERROR_MESSAGES.FILE_TOO_SMALL,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    if (this.signFileType === 'pdf') {
      const invalidTypeFiles = newFiles.filter(file => {
        const isValidMimeType = ALLOWED_FILE_TYPES.PDF.mimeTypes.includes(file.type as 'application/pdf');
        const isValidExtension = file.name.toLowerCase().endsWith(FILE_EXTENSIONS.PDF);
        return !isValidMimeType && !isValidExtension;
      });

      if (invalidTypeFiles.length > 0) {
        Swal.fire({
          icon: 'error',
          title: 'Tipo de archivo inválido',
          text: FILE_ERROR_MESSAGES.INVALID_PDF,
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
        return;
      }
    }

    const uniqueNewFiles = newFiles.filter(newFile =>
      !existingFiles.some(existingFile =>
        existingFile.name === newFile.name && existingFile.size === newFile.size
      )
    );

    if (uniqueNewFiles.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Archivos duplicados',
        text: 'Los archivos seleccionados ya están en la lista.',
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    const combinedFiles = [...existingFiles, ...uniqueNewFiles];

    if (combinedFiles.length > FILE_LIMITS.MAX_FILES) {
      Swal.fire({
        icon: 'error',
        title: 'Demasiados archivos',
        text: `Máximo ${FILE_LIMITS.MAX_FILES} archivos permitidos. Actualmente tienes ${existingFiles.length} y estás intentando agregar ${uniqueNewFiles.length}.`,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    this.signStateService.setFiles(combinedFiles);
    this.convertFilesToBase64(uniqueNewFiles, existingFiles.length);

    if (hadNoFiles) {
      setTimeout(() => this.scrollToSignFilesStep(), 80);
    }
  }

  private scrollToSignFilesStep(): void {
    this.signFilesStep?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  removeSignFile(index: number): void {
    this.signStateService.removeFile(index);
    const state = this.signStateService.currentState;
    if (state.files.length === 0) {
      this.signStateService.resetSignSection();
      this.resetSignFileInputs();
    }
  }

  clearAllSignFiles(): void {
    this.signStateService.resetSignSection();
    this.resetSignFileInputs();
  }

  private resetSignFileInputs(): void {
    if (this.pdfFileInput?.nativeElement) {
      this.pdfFileInput.nativeElement.value = '';
    }
    if (this.otherFileInput?.nativeElement) {
      this.otherFileInput.nativeElement.value = '';
    }
  }

  convertFilesToBase64(files: File[], startIndex: number = 0): void {
    const state = this.signStateService.currentState;
    const existingPdfBase64s = state.pdfBase64s || [];
    const existingCadesBase64s = state.cadesBase64s || [];
    const newPdfBase64s: string[] = [];
    const newCadesBase64s: string[] = [];
    let completedCount = 0;
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        if (state.fileType === 'pdf') {
          newPdfBase64s.push(base64String);
        } else {
          newCadesBase64s.push(base64String);
        }
        
        completedCount++;
        
        // Cuando todos los archivos se han convertido, actualizar el estado
        if (completedCount === files.length) {
          if (state.fileType === 'pdf') {
            // Combinar los existentes con los nuevos
            const combinedPdfBase64s = [...existingPdfBase64s, ...newPdfBase64s];
            this.signStateService.setPdfBase64s(combinedPdfBase64s);
          } else {
            // Combinar los existentes con los nuevos
            const combinedCadesBase64s = [...existingCadesBase64s, ...newCadesBase64s];
            this.signStateService.setCadesBase64s(combinedCadesBase64s);
          }
        }
      };
      reader.onerror = () => {
        completedCount++;
        console.error(`Error al leer el archivo: ${file.name}`);
        if (completedCount === files.length) {
          // Aun así actualizar el estado con los que se pudieron convertir
          if (state.fileType === 'pdf') {
            const combinedPdfBase64s = [...existingPdfBase64s, ...newPdfBase64s];
            this.signStateService.setPdfBase64s(combinedPdfBase64s);
          } else {
            const combinedCadesBase64s = [...existingCadesBase64s, ...newCadesBase64s];
            this.signStateService.setCadesBase64s(combinedCadesBase64s);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  }

  signDocuments(): void {
    if (this.signFiles.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: FILE_ERROR_MESSAGES.SIGN_NO_FILES,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    if (this.signFileType === 'pdf' && this.hasPdfVisiblePendingConfiguration()) {
      Swal.fire({
        icon: 'warning',
        title: 'Firma visible incompleta',
        html: 'Marcaste <strong>firma visible</strong> en uno o más PDF pero falta dibujar la zona de firma. Pulsa <strong>Dibujar zona de firma</strong> y completa cada documento, o desmarca los que no lleven firma visible.',
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    // Certifirma Digital Físico (agente disponible): certificados → elegir uno → PAdES/CAdES → mismo resultado que otros
    if (this.showTokenFisicoSignFlow) {
      this.startTokenFisicoSign();
      return;
    }

    // Firma RSE: multipart con File (sin convertir a base64). Modal preparing → Entrust.
    if (this.signMechanism === 'rse') {
      this.pendingRseAuthPopup = this.openBlankRseAuthWindow(
        this.signFiles[0]?.name || 'documento'
      );
      this.openRsePreparingDialog(this.signFiles[0]?.name || 'documento');
      this.startRseSign();
      return;
    }

    const state = this.signStateService.currentState;
    const hasVisibleSignatureReady =
      this.signFileType === 'pdf' &&
      this.pdfWantVisibleSignature.some((w, i) => w && !!this.pdfVisibleSignatureConfigs[i]);

    // Ya autenticado al definir firma visible: firmar sin volver a pedir credenciales
    if (hasVisibleSignatureReady) {
      if (this.signCertificateType === 'token') {
        if (state.selectedCertificate && state.user && state.password) {
          this.uiHelper.showSpinner('Firmando documentos...', 'sign');
          this.ensurePdfBase64sThenSign(() => this.signAllPdfs());
          return;
        }
      } else if (state.uploadedCertificate && state.uploadedCertificatePassword) {
        this.uiHelper.showSpinner('Firmando documentos con certificado P10...', 'sign');
        this.ensurePdfBase64sThenSign(() => this.signAllP10Pdf());
        return;
      }
    }

    // Abrir directamente el modal correcto según el tipo seleccionado
    if (this.signCertificateType === 'token') {
      this.authPurpose = 'sign';
      this.openAuthModal();
    } else {
      this.authPurpose = 'sign';
      this.openPkcs12UploadModal();
    }
  }

  /**
   * Flujo RSE (BFF Certitool → Sign_RSE): un proceso por documento vía multipart,
   * abre authorizationUrl, hace polling y descarga el resultado firmado.
   */
  startRseSign(): void {
    const state = this.signStateService.currentState;
    if (!state.files.length) {
      this.uiHelper.hideSpinner();
      this.rseActiveAuthDialog?.close({ status: 'cancel' });
      this.clearRseActiveAuthDialog();
      this.closePendingRseAuthPopup();
      Swal.fire({
        icon: 'warning',
        title: 'Sin documentos',
        text: 'No hay archivos listos para firmar. Vuelve a cargar el documento en la sección de firmar.',
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    const identificationNumber = (state.user || this.auditIdentificationNumber || '').trim();
    this.continueRseSignFlow(identificationNumber || undefined);
  }

  /** Abre ventana en blanco en el gesto del usuario (evita bloqueo de popups). */
  private openBlankRseAuthWindow(fileName?: string | null): Window | null {
    const w = 900;
    const h = 650;
    const left = Math.max(0, Math.round((window.screen.width - w) / 2));
    const top = Math.max(0, Math.round((window.screen.height - h) / 2));
    try {
      const popup = window.open(
        'about:blank',
        'certitool_rse_auth',
        `popup=yes,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      if (popup) {
        try {
          const iconUrl = `${window.location.origin}/assets/icons/signature-icon.svg`;
          const html = buildRseAuthPreparingHtml({
            fileName: fileName || this.signFiles[0]?.name || null,
            iconUrl
          });
          popup.document.open();
          popup.document.write(html);
          popup.document.close();
        } catch {
          // ignore cross-origin / blank restrictions
        }
        popup.focus();
      }
      return popup;
    } catch {
      return null;
    }
  }

  private consumePendingRseAuthPopup(): Window | null {
    const popup = this.pendingRseAuthPopup;
    this.pendingRseAuthPopup = null;
    if (popup && popup.closed) {
      return null;
    }
    return popup;
  }

  private closePendingRseAuthPopup(): void {
    const popup = this.consumePendingRseAuthPopup();
    try {
      popup?.close();
    } catch {
      // ignore
    }
  }

  /** Abre de inmediato el modal RSE en estado preparing (gesto Continuar). */
  private openRsePreparingDialog(fileName: string): void {
    if (this.rseActiveAuthDialog) {
      return;
    }
    this.uiHelper.hideSpinner();
    const preopenedPopup = this.consumePendingRseAuthPopup();
    this.rseActiveAuthDialog = this.dialog.open(RseAuthModalComponent, {
      width: '440px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      hasBackdrop: true,
      backdropClass: 'rse-auth-backdrop',
      panelClass: ['custom-dialog', 'rse-auth-dialog'],
      enterAnimationDuration: '560ms',
      exitAnimationDuration: '320ms',
      data: {
        authorizationUrl: '',
        processId: '',
        fileName,
        preparing: true,
        autoStart: false,
        preopenedPopup
      } as RseAuthModalData
    });
    this.rseActiveAuthDialog.afterClosed().subscribe(() => {
      if (this.rseActiveAuthDialog) {
        this.rseActiveAuthDialog = null;
      }
      this.closePendingRseAuthPopup();
      this.uiHelper.unlockUi();
    });
  }

  private clearRseActiveAuthDialog(): void {
    this.rseActiveAuthDialog = null;
  }

  private continueRseSignFlow(identificationNumber?: string): void {
    const state = this.signStateService.currentState;
    const isPdf = this.signFileType === 'pdf';
    const files = state.files;

    const signatureType: SignRseSignatureType = isPdf ? 'PADES' : 'CADES';
    const total = files.length;
    const fileNames = files.map((f, i) => f.name || `documento_${i + 1}`);

    // No usar setSignProgress aquí: abre el spinner global y tapa el modal RSE
    // (impedía ver «Autenticación incompleta» / Cancelar al cerrar Entrust).
    this.uiHelper.hideSpinner();

    from(files.map((file, index) => ({ file, index }))).pipe(
      concatMap(({ file, index }) => {
        let authDialogRef = index === 0 ? this.rseActiveAuthDialog : null;
        if (!authDialogRef) {
          const preopenedPopup =
            index === 0
              ? this.consumePendingRseAuthPopup()
              : this.openBlankRseAuthWindow(fileNames[index]);
          authDialogRef = this.dialog.open(RseAuthModalComponent, {
            width: '440px',
            maxWidth: '95vw',
            disableClose: true,
            autoFocus: false,
            hasBackdrop: true,
            backdropClass: 'rse-auth-backdrop',
            panelClass: ['custom-dialog', 'rse-auth-dialog'],
            enterAnimationDuration: '560ms',
            exitAnimationDuration: '320ms',
            data: {
              authorizationUrl: '',
              processId: '',
              fileName: fileNames[index],
              preparing: true,
              autoStart: false,
              preopenedPopup
            } as RseAuthModalData
          });
          if (index === 0) {
            this.rseActiveAuthDialog = authDialogRef;
          }
        } else if (index === 0) {
          // Reutiliza el modal abierto en el clic; actualiza nombre si aplica
          try {
            authDialogRef.componentInstance.fileName = fileNames[index];
          } catch {
            // ignore
          }
        }

        // Si el usuario cancela mientras create sigue en curso, no reabrir Entrust.
        let authCancelledEarly = false;
        const earlyCancelSub = authDialogRef.afterClosed().subscribe((result) => {
          if (!result || result.status === 'cancel' || result.status === 'failed') {
            authCancelledEarly = true;
            this.uiHelper.unlockUi();
          }
        });

        return from(
          this.buildRseMultipartForm(
            signatureType,
            file,
            fileNames[index],
            index,
            identificationNumber
          )
        ).pipe(
          concatMap((formData) => this.rseSignService.createFromMultipart(formData)),
          concatMap((created) => {
            if (authCancelledEarly) {
              if (created?.id) {
                this.rseSignService.cancel(created.id).subscribe({ error: () => {} });
              }
              this.uiHelper.unlockUi();
              return of({
                ok: false as const,
                error: new Error('Autenticación con código de verificación cancelada por el usuario.'),
                index
              });
            }

            const status = String(created.status || '').toUpperCase();
            const terminal = status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELED';
            const needsAuthUi = !!created.authorizationUrl && !terminal;

            if (!needsAuthUi) {
              authDialogRef.close();
              this.uiHelper.showSignJourney({
                phase: 'prepare',
                current: index + 1,
                total,
                fileName: fileNames[index],
                fileSizeBytes: file.size ?? null,
                mensaje: `Descargando y preparando la vista (${fileNames[index]})…`
              });
              return this.rseSignService.waitAndDownload(created.id).pipe(
                map(({ blob, process, fileName }) => {
                  this.captureAuditFromRseProcess(process);
                  return {
                    ok: true as const,
                    blob,
                    index,
                    signedFileName: this.resolveRseSignedDisplayName(
                      fileNames[index],
                      process,
                      fileName
                    )
                  };
                }),
                catchError((error) => {
                  this.uiHelper.hideSpinner();
                  if (this.isAuthSignError(error)) {
                    return throwError(() => error);
                  }
                  return of({ ok: false as const, error, index });
                })
              );
            }

            try {
              authDialogRef.componentInstance.beginExternalAuth(
                created.authorizationUrl!,
                created.id
              );
            } catch {
              this.uiHelper.unlockUi();
              return of({
                ok: false as const,
                error: new Error('Autenticación con código de verificación cancelada por el usuario.'),
                index
              });
            }

            return authDialogRef.afterClosed().pipe(
              map((result: RseAuthModalResult | undefined) => {
                if (result?.status === 'completed') {
                  // No unlockUi aquí: el journey de preparación debe seguir visible.
                  this.captureAuditFromRseProcess(result.process);
                  return {
                    ok: true as const,
                    blob: result.blob,
                    index,
                    signedFileName: this.resolveRseSignedDisplayName(
                      fileNames[index],
                      result.process,
                      result.signedFileName
                    )
                  };
                }
                this.uiHelper.unlockUi();
                if (result?.status === 'failed') {
                  return { ok: false as const, error: result.error, index };
                }
                return {
                  ok: false as const,
                  error: new Error('Autenticación con código de verificación cancelada por el usuario.'),
                  index
                };
              })
            );
          }),
          catchError((error) => {
            this.closePendingRseAuthPopup();
            this.uiHelper.unlockUi();
            try {
              authDialogRef.close({ status: 'failed', error });
            } catch {
              // ignore
            }
            if (index === 0) {
              this.clearRseActiveAuthDialog();
            }
            if (this.isAuthSignError(error)) {
              return throwError(() => error);
            }
            return of({ ok: false as const, error, index });
          }),
          finalize(() => {
            earlyCancelSub.unsubscribe();
            this.uiHelper.unlockUi();
          })
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (outcomes) => {
        this.clearRseActiveAuthDialog();
        this.closePendingRseAuthPopup();
        this.uiHelper.unlockUi();
        const signedSrcs: string[] = [];
        const signedNames: string[] = [];
        const failedMessages: string[] = [];
        const anyOk = outcomes.some((o) => o.ok);

        // Tras COMPLETED (sobre todo archivos grandes): mantener viaje visible
        // mientras convertimos el blob y abrimos el modal de éxito.
        if (anyOk) {
          const firstOk = outcomes.find((o) => o.ok);
          const idx = firstOk?.index ?? 0;
          this.uiHelper.showSignJourney({
            phase: 'prepare',
            current: idx + 1,
            total,
            fileName: fileNames[idx],
            fileSizeBytes: files[idx]?.size ?? null,
            mensaje: `Preparando la vista del documento (${fileNames[idx]})…`
          });
        }

        for (const outcome of outcomes) {
          const name = fileNames[outcome.index] || `documento_${outcome.index + 1}`;
          if (!outcome.ok) {
            failedMessages.push(`${name}: ${this.resolveSignHttpErrorMessage(outcome.error)}`);
            continue;
          }
          try {
            this.uiHelper.updateSignJourney({
              phase: 'prepare',
              current: outcome.index + 1,
              total,
              fileName: name,
              fileSizeBytes: files[outcome.index]?.size ?? null
            });
            const dataUrl = await this.blobToDataUrl(outcome.blob);
            signedSrcs.push(dataUrl);
            const displayName =
              'signedFileName' in outcome && outcome.signedFileName
                ? outcome.signedFileName
                : name;
            signedNames.push(displayName);
          } catch {
            failedMessages.push(`${name}: No se pudo leer el documento firmado.`);
          }
        }

        if (!signedSrcs.length) {
          this.uiHelper.hideSpinner();
        }

        this.finishSequentialSign({
          signedSrcs,
          signedNames,
          failedMessages,
          fileType: isPdf ? 'pdf' : 'other'
        });
      },
      error: (error) => {
        this.clearRseActiveAuthDialog();
        this.closePendingRseAuthPopup();
        this.uiHelper.hideSpinner();
        this.uiHelper.unlockUi();
        this.showSignFatalError(error);
      }
    });
  }

  /** FormData multipart hacia BFF `/api/sign/rse/processes/multipart`. */
  private buildRseMultipartForm(
    signatureType: SignRseSignatureType,
    file: File,
    fileName: string,
    fileIndex: number,
    identificationNumber?: string
  ): Promise<FormData> {
    const defaultType = signatureType === 'PADES' ? 'application/pdf' : 'application/octet-stream';
    const document =
      file.type && file.type.trim()
        ? file
        : new File([file], fileName || file.name || 'document', { type: defaultType });

    let appearance: SignRseAppearance | undefined;
    if (
      signatureType === 'PADES' &&
      this.pdfWantVisibleSignature[fileIndex] &&
      this.pdfVisibleSignatureConfigs[fileIndex]
    ) {
      const entry = this.pdfVisibleSignatureConfigs[fileIndex]!;
      appearance = this.buildRseAppearance(
        entry.zone,
        entry.imageChoice,
        entry.visibleSignature?.text
      );
    }

    let outputExtension: string | undefined;
    if (signatureType === 'CADES') {
      const configured = (environment as { signRseCadesOutputExtension?: string })
        .signRseCadesOutputExtension;
      outputExtension = normalizeCadesOutputExtension(configured);
    }

    return this.rseSignService.buildCreateMultipartForm({
      document,
      fileName: fileName || document.name,
      signatureType,
      origin: 'certitool-web',
      identificationNumber: identificationNumber?.trim() || undefined,
      outputExtension,
      appearance
    });
  }

  /**
   * Contrato Sign_RSE:
   * - imagen propia o sello Adobe del cliente → imageBase64
   * - sin imagen usable → useCerticamaraStamp: true (CN/fecha los arma Sign_RSE/RSE)
   */
  private buildRseAppearance(
    zone: PadesSignatureZoneResult,
    imageChoice?: PadesSignatureImageChoice,
    visibleText?: string
  ): SignRseAppearance {
    const width = Math.max(0, zone.upperRightX - zone.lowerLeftX);
    const height = Math.max(0, zone.upperRightY - zone.lowerLeftY);
    const base: SignRseAppearance = {
      pageNumber: zone.signPage,
      x: zone.lowerLeftX,
      y: zone.lowerLeftY,
      width,
      height
    };

    const stamp = imageChoice?.imageBase64?.trim();
    const hasUsableClientStamp = !!stamp
      && (
        imageChoice?.mode === 'upload'
        || !!this.sanitizeVisibleSignerCn(visibleText)
      );

    if (hasUsableClientStamp && stamp) {
      return {
        ...base,
        imageBase64: this.stripDataUrlPrefix(stamp)
      };
    }

    return {
      ...base,
      useCerticamaraStamp: true
    };
  }

  private stripDataUrlPrefix(value: string): string {
    const comma = value.indexOf(',');
    if (value.startsWith('data:') && comma >= 0) {
      return value.slice(comma + 1);
    }
    return value;
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private captureAuditFromMultipart(result?: SignMultipartResult | null): void {
    this.captureAuditFromResponse({
      transactionId: result?.transactionId,
      serial: result?.serial,
      identificationNumber: result?.identificationNumber
    });
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const raw = this.stripDataUrlPrefix((base64 || '').trim());
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  private async buildPdfMultipartAppearanceParams(index: number): Promise<{
    appearanceEnabled: boolean;
    mode?: string;
    signFieldName?: string;
    llx?: number;
    lly?: number;
    urx?: number;
    ury?: number;
    page?: number;
    contentSignature?: string;
    imageBase64?: string | null;
    imageValidation?: boolean;
    timestampEnabled: boolean;
    ltvEnabled: boolean;
  }> {
    const entry = this.pdfVisibleSignatureConfigs[index];
    const tsaLtv = this.resolveSignTsaLtvFlags();
    if (!(this.pdfWantVisibleSignature[index] && entry)) {
      return { appearanceEnabled: false, ...tsaLtv };
    }
    const certificateOwnerName = this.getVisibleSignatureTextForEntry(entry);
    const imageChoice = await this.resolveImageChoiceForSign(entry, certificateOwnerName);
    const appearance = this.buildPdfVisibleAppearance(
      entry.zone,
      imageChoice,
      certificateOwnerName
    );
    return {
      appearanceEnabled: true,
      mode: appearance.mode,
      signFieldName: appearance.signFieldName,
      llx: appearance.rect.llx,
      lly: appearance.rect.lly,
      urx: appearance.rect.urx,
      ury: appearance.rect.ury,
      page: appearance.page,
      contentSignature: appearance.contentSignature,
      imageBase64: appearance.imageBase64
        ? this.stripDataUrlPrefix(appearance.imageBase64)
        : null,
      imageValidation: appearance.imageValidation,
      ...tsaLtv
    };
  }

  /**
   * Regenera el sello Adobe con el CN real cuando el modo es default
   * (evita dejar “Nombre del firmante” horneado en la PNG).
   */
  private async resolveImageChoiceForSign(
    entry: PdfVisibleSignatureEntry,
    signerCn: string
  ): Promise<PadesSignatureImageChoice> {
    const choice = entry.imageChoice;
    if (choice?.mode === 'upload' && choice.imageBase64?.trim()) {
      return choice;
    }
    const widthPt = Math.max(entry.zone.upperRightX - entry.zone.lowerLeftX, 1);
    const heightPt = Math.max(entry.zone.upperRightY - entry.zone.lowerLeftY, 1);
    const cn = this.sanitizeVisibleSignerCn(signerCn) || this.visibleSignatureFallbackText;
    const imageBase64 = await buildAdobeStyleStampImageBase64(cn, new Date(), { widthPt, heightPt });
    return {
      useDefault: true,
      mode: 'default',
      imageBase64: imageBase64 || choice?.imageBase64
    };
  }

  /**
   * Flags TSA/LTV del lote.
   * MVP: forzados a false (UI de selección comentada). Al reactivar:
   * timestampEnabled = !!this.signTimestampEnabled;
   * ltvEnabled = this.signFileType === 'pdf' && timestampEnabled && !!this.signLtvEnabled;
   */
  private resolveSignTsaLtvFlags(): { timestampEnabled: boolean; ltvEnabled: boolean } {
    return { timestampEnabled: false, ltvEnabled: false };
  }

  /** MVP: UI TSA comentada; se mantiene para reactivar en otra rama. */
  setSignTimestampEnabled(enabled: boolean): void {
    this.signTimestampEnabled = !!enabled;
    if (!this.signTimestampEnabled) {
      this.signLtvEnabled = false;
    }
  }

  /** MVP: UI LTV comentada; se mantiene para reactivar en otra rama. */
  setSignLtvEnabled(enabled: boolean): void {
    if (this.signFileType !== 'pdf') {
      this.signLtvEnabled = false;
      return;
    }
    this.signLtvEnabled = !!enabled;
    if (this.signLtvEnabled) {
      this.signTimestampEnabled = true;
    }
  }

  private async finishSequentialSignFromMultipart(opts: {
    outcomes: Array<
      | { ok: true; result: SignMultipartResult; index: number }
      | { ok: false; error: unknown; index: number }
    >;
    fileNames: string[];
    fileType: 'pdf' | 'other';
    mimeForDataUrl: string;
  }): Promise<void> {
    const { outcomes, fileNames, fileType, mimeForDataUrl } = opts;
    const signedSrcs: string[] = [];
    const signedNames: string[] = [];
    const failedMessages: string[] = [];
    const total = fileNames.length;

    for (const outcome of outcomes) {
      const name = fileNames[outcome.index] || `documento_${outcome.index + 1}`;
      if (!outcome.ok) {
        failedMessages.push(`${name}: ${this.resolveSignHttpErrorMessage(outcome.error)}`);
        continue;
      }
      try {
        this.uiHelper.updateSignJourney({
          phase: 'prepare',
          current: outcome.index + 1,
          total,
          fileName: name
        });
        const dataUrl = await this.blobToDataUrl(
          outcome.result.blob.type
            ? outcome.result.blob
            : new Blob([outcome.result.blob], { type: mimeForDataUrl })
        );
        signedSrcs.push(dataUrl);
        signedNames.push(outcome.result.fileName?.trim() || name);
      } catch {
        failedMessages.push(`${name}: No se pudo leer el documento firmado.`);
      }
    }

    this.finishSequentialSign({
      signedSrcs,
      signedNames,
      failedMessages,
      fileType
    });
  }

  /**
   * Flujo de firma con Certifirma Digital Físico: obtener solo certificados del token (usb_token), no del keystore → modal de selección → PAdES o CAdES → modal de resultado (descarga/verificar).
   */
  startTokenFisicoSign(): void {
    this.uiHelper.showSpinner('Cargando certificados del token...', 'auth');
    this.localAgentService.getCertificates('usb_token')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (certificates) => {
          this.uiHelper.hideSpinner();
          if (!certificates?.length) {
            this.openAlertModal('warning', 'Sin certificados en el token', 'No hay certificados en el token. Conecte el token, compruebe el complemento e intente de nuevo.');
            return;
          }
          const dialogRef = this.dialog.open(TokenFisicoCertificateModalComponent, {
            width: '600px',
            maxHeight: '80vh',
            panelClass: 'custom-dialog',
            data: { certificates } as TokenFisicoCertificateModalData
          });
          dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: { certificate?: LocalAgentCertificate } | undefined) => {
              const selectedCertificate = result?.certificate;
              if (!selectedCertificate?.thumbprint) {
                return;
              }
              const thumbprint = selectedCertificate.thumbprint;
              const fileType = this.signFileType;
              const files = this.signFiles;
              this.uiHelper.showSpinner('Firmando documentos...', 'sign');
              const certCn = this.cnFromLocalAgentCertificate(selectedCertificate);
              if (certCn) {
                this.visibleSignerCn = certCn;
              }
              this.readFilesAsBase64(files).then(
                async (base64List) => {
                  const isPdf = fileType === 'pdf';
                  if (isPdf) {
                    const documents: LocalAgentPdfDocument[] = [];
                    for (let idx = 0; idx < base64List.length; idx++) {
                      const b64 = base64List[idx];
                      const entry = this.pdfVisibleSignatureConfigs[idx];
                      const useVis =
                        this.pdfWantVisibleSignature[idx] && entry;
                      if (!useVis || !entry) {
                        documents.push({ pdfBase64: b64 });
                        continue;
                      }
                      const visibleText = certCn
                        || this.getVisibleSignatureTextForEntry(entry);
                      const imageChoice = await this.resolveImageChoiceForSign(
                        entry,
                        visibleText
                      );
                      const stamp = imageChoice.imageBase64?.trim();
                      const doc: LocalAgentPdfDocument = {
                        pdfBase64: b64,
                        signReason: '',
                        signLocation: '',
                        signFieldName: this.generateUniqueSignFieldName(),
                        lowerLeftX: entry.zone.lowerLeftX,
                        lowerLeftY: entry.zone.lowerLeftY,
                        upperRightX: entry.zone.upperRightX,
                        upperRightY: entry.zone.upperRightY,
                        signPage: entry.zone.signPage,
                        contentSignature: buildAdobeStampDescription(visibleText),
                        renderingMode: stamp
                          ? 'GRAPHIC_AND_DESCRIPTION'
                          : 'NAME_AND_DESCRIPTION',
                        imageValidation: true
                      };
                      if (stamp) {
                        doc.signatureImageBase64 = this.stripDataUrlPrefix(stamp);
                      }
                      documents.push(doc);
                    }
                    this.doTokenFisicoSignPdf(
                      thumbprint,
                      documents,
                      files.map((f, i) => f.name || `documento_${i + 1}`)
                    );
                  } else {
                    const documents = base64List.map((b64, i) => ({
                      contentBase64: b64,
                      fileName: files[i]?.name
                    }));
                    this.doTokenFisicoSignCades(
                      thumbprint,
                      documents,
                      files.map((f, i) => f.name || `documento_${i + 1}`)
                    );
                  }
                },
                () => {
                  this.uiHelper.hideSpinner();
                  this.showTokenFisicoSignError('Error al leer los archivos.');
                }
              );
            });
        },
        error: (err) => {
          this.uiHelper.hideSpinner();
          this.showTokenFisicoSignError(err?.message || 'No se pudieron cargar los certificados del complemento.');
        }
      });
  }

  private showTokenFisicoSignError(message: string): void {
    Swal.fire({
      icon: 'error',
      title: 'Error al firmar',
      text: message,
      confirmButtonText: 'Entendido',
      ...this.notificationService.getSwalBaseConfig()
    });
  }

  private doTokenFisicoSignPdf(thumbprint: string, documents: LocalAgentPdfDocument[], fileNames: string[]): void {
    const total = documents.length;
    this.setSignProgress(1, total, fileNames[0]);

    from(documents.map((doc, index) => ({ doc, index }))).pipe(
      concatMap(({ doc, index }) => {
        this.setSignProgress(index + 1, total, fileNames[index]);
        return this.localAgentService.signPdf(thumbprint, [doc]).pipe(
          map(signedB64List => ({ ok: true as const, signedB64List, index })),
          catchError(error => of({ ok: false as const, error, index }))
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (outcomes) => {
        const signedSrcs: string[] = [];
        const signedNames: string[] = [];
        const failedMessages: string[] = [];

        for (const outcome of outcomes) {
          const name = fileNames[outcome.index] || `documento_${outcome.index + 1}`;
          if (!outcome.ok) {
            failedMessages.push(`${name}: ${outcome.error?.message || FILE_ERROR_MESSAGES.SIGN_ERROR}`);
            continue;
          }
          const b64 = outcome.signedB64List?.[0];
          if (b64) {
            signedSrcs.push(`data:application/pdf;base64,${b64}`);
            signedNames.push(name);
          } else {
            failedMessages.push(`${name}: ${FILE_ERROR_MESSAGES.SIGN_FAILED}`);
          }
        }

        this.finishSequentialSign({
          signedSrcs,
          signedNames,
          failedMessages,
          fileType: 'pdf'
        });
      },
      error: (err) => {
        this.uiHelper.hideSpinner();
        this.showTokenFisicoSignError(err?.message || FILE_ERROR_MESSAGES.SIGN_ERROR);
      }
    });
  }

  private doTokenFisicoSignCades(
    thumbprint: string,
    documents: { contentBase64: string; fileName?: string }[],
    fileNames: string[]
  ): void {
    const total = documents.length;
    this.setSignProgress(1, total, fileNames[0]);

    from(documents.map((doc, index) => ({ doc, index }))).pipe(
      concatMap(({ doc, index }) => {
        this.setSignProgress(index + 1, total, fileNames[index]);
        return this.localAgentService.signCades(thumbprint, [doc]).pipe(
          map(signedB64List => ({ ok: true as const, signedB64List, index })),
          catchError(error => of({ ok: false as const, error, index }))
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (outcomes) => {
        const signedSrcs: string[] = [];
        const signedNames: string[] = [];
        const failedMessages: string[] = [];

        for (const outcome of outcomes) {
          const name = fileNames[outcome.index] || `documento_${outcome.index + 1}`;
          if (!outcome.ok) {
            failedMessages.push(`${name}: ${outcome.error?.message || FILE_ERROR_MESSAGES.SIGN_ERROR}`);
            continue;
          }
          const b64 = outcome.signedB64List?.[0];
          if (b64) {
            signedSrcs.push(`data:application/octet-stream;base64,${b64}`);
            signedNames.push(name);
          } else {
            failedMessages.push(`${name}: ${FILE_ERROR_MESSAGES.SIGN_FAILED}`);
          }
        }

        this.finishSequentialSign({
          signedSrcs,
          signedNames,
          failedMessages,
          fileType: 'other'
        });
      },
      error: (err) => {
        this.uiHelper.hideSpinner();
        this.showTokenFisicoSignError(err?.message || FILE_ERROR_MESSAGES.SIGN_ERROR);
      }
    });
  }

  private readFilesAsBase64(files: File[]): Promise<string[]> {
    return Promise.all(
      files.map((file) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            const b64 = dataUrl.indexOf(',') >= 0 ? dataUrl.split(',')[1] : dataUrl;
            resolve(b64 || '');
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        })
      )
    );
  }

  openAuthModal(): void {
    const dialogRef = this.dialog.open(AuthModalComponent, {
      width: '600px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: '80vh',
      panelClass: 'custom-dialog',
      data: { certificates: this.certificates }
    });

    dialogRef.componentInstance.credentialsEntered
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ nuip, password }) => {
        this.user = nuip;
        this.password = password;
        dialogRef.componentInstance.isLoading = true;
        this.uiHelper.showSpinner('Cargando certificados...', 'auth');

        this.certificateService.getCertificates(nuip, password)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (!response.success) {
                const errorMessage = response.message || FILE_ERROR_MESSAGES.INVALID_CREDENTIALS;
                this.openAlertModal('error', 'Error de Autenticación', errorMessage);
                dialogRef.componentInstance.isLoading = false;
                this.uiHelper.hideSpinner();
                return;
              }
              const certificates = (response.certificates || []).map((c) =>
                this.certificateService.mapApiCertificateToModel(c)
              );
              this.signStateService.setCertificates(certificates);
              this.signStateService.setCredentials(nuip, password);
              dialogRef.componentInstance.data.certificates = certificates;
              dialogRef.componentInstance.isAuthenticated = true;
              dialogRef.componentInstance.isLoading = false;
              this.uiHelper.hideSpinner();
              // Leer archivos a base64 en el cliente (el nuevo endpoint no los devuelve)
              const filesToRead = this.signFiles;
              if (filesToRead.length > 0) {
                this.readFilesAsBase64(filesToRead).then(
                  (base64List) => {
                    if (this.signFileType === 'pdf') {
                      this.signStateService.setPdfBase64s(base64List);
                    } else {
                      this.signStateService.setCadesBase64s(base64List);
                    }
                  },
                  () => {
                    this.openAlertModal('error', 'Error', 'No se pudieron leer los archivos.');
                  }
                );
              }
            },
            error: (error) => {
              const errorMessage =
                error?.error?.message ||
                error?.message ||
                FILE_ERROR_MESSAGES.INVALID_CREDENTIALS;
              this.openAlertModal('error', 'Error de Autenticación', errorMessage);
              dialogRef.componentInstance.isLoading = false;
              this.uiHelper.hideSpinner();
            }
          });
      });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.selectedCertificate) {
          this.handleCertificateSelected(result.selectedCertificate);
        } else if (this.authPurpose === 'firmaVisible') {
          this.authPurpose = 'sign';
          this.pendingFirmaVisibleFileIndex = null;
        }
      });
  }

  openPkcs12UploadModal(): void {
    const dialogRef = this.dialog.open(UploadP12ModalComponent, {
      width: '600px',
      maxHeight: '80vh',
      panelClass: 'custom-dialog'
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result?.certificateFile) {
          if (this.authPurpose === 'firmaVisible') {
            this.authPurpose = 'sign';
            this.pendingFirmaVisibleFileIndex = null;
          }
          return;
        }
        if (this.authPurpose === 'firmaVisible') {
          this.prepareP10FirmaVisible(result.certificateFile as File, result.password as string);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = (reader.result as string).split(',')[1];
          if (!base64String) {
            this.handleP10ReadError();
            return;
          }
          this.handleCertificateP10Selected(base64String, result.password);
        };
        reader.onerror = () => this.handleP10ReadError();
        reader.readAsDataURL(result.certificateFile);
      });
  }

  private prepareP10FirmaVisible(certFile: File, password: string): void {
    this.uiHelper.showSpinner('Leyendo certificado...', 'auth');
    this.certificateService.inspectPfxIdentity(certFile, password)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.uiHelper.hideSpinner())
      )
      .subscribe({
        next: (identity) => {
          const reader = new FileReader();
          reader.onload = () => {
            const certBase64 = (reader.result as string).split(',')[1];
            if (!certBase64) {
              this.handleP10ReadError();
              return;
            }
            const cn = this.sanitizeVisibleSignerCn(identity?.cn)
              || this.visibleSignatureFallbackText;
            this.handleCertificateP10Selected(certBase64, password, cn);
          };
          reader.onerror = () => this.handleP10ReadError();
          reader.readAsDataURL(certFile);
        },
        error: () => {
          // Si el inspect falla, igual cargamos el P12 y pedimos CN editable en el modal
          const reader = new FileReader();
          reader.onload = () => {
            const certBase64 = (reader.result as string).split(',')[1];
            if (!certBase64) {
              this.handleP10ReadError();
              return;
            }
            this.signStateService.setUploadedCertificate(certBase64, password);
            const fileIndex = this.pendingFirmaVisibleFileIndex;
            this.authPurpose = 'sign';
            this.pendingFirmaVisibleFileIndex = null;
            if (fileIndex != null) {
              this.openFirmaVisibleModalInternal(fileIndex, '', { signerCnEditable: true });
            }
          };
          reader.onerror = () => this.handleP10ReadError();
          reader.readAsDataURL(certFile);
        }
      });
  }

  private handleP10ReadError(): void {
    this.uiHelper.hideSpinner();
    this.notificationService.cleanupSwal();
    Swal.fire({
      icon: 'error',
      title: 'Certificado no válido',
      text: 'No se pudo leer el archivo del certificado.',
      confirmButtonText: 'Entendido',
      ...this.notificationService.getSwalBaseConfig()
    });
    this.authPurpose = 'sign';
    this.pendingFirmaVisibleFileIndex = null;
  }

  handleCertificateSelected(cert: Certificate): void {
    this.signStateService.selectCertificate(cert);
    this.visibleSignerCn = this.sanitizeVisibleSignerCn(cert.cn)
      || this.visibleSignatureFallbackText;

    if (this.authPurpose === 'firmaVisible') {
      this.authPurpose = 'sign';
      const fileIndex = this.pendingFirmaVisibleFileIndex;
      this.pendingFirmaVisibleFileIndex = null;
      if (fileIndex != null) {
        this.openFirmaVisibleModalInternal(fileIndex, this.visibleSignerCn);
      }
      return;
    }

    this.uiHelper.showSpinner('Firmando documentos...', 'sign');
    const state = this.signStateService.currentState;
    if (state.fileType === 'other') {
      this.signAllCades();
    } else if (state.fileType === 'pdf') {
      this.signAllPdfs();
    } else {
      this.uiHelper.hideSpinner();
    }
  }

  handleCertificateP10Selected(certBase64: string, password: string, signerCn?: string): void {
    this.signStateService.setUploadedCertificate(certBase64, password);
    const cn = this.sanitizeVisibleSignerCn(signerCn)
      || this.sanitizeVisibleSignerCn(this.visibleSignerCn)
      || this.visibleSignatureFallbackText;
    this.visibleSignerCn = cn;

    if (this.authPurpose === 'firmaVisible') {
      this.authPurpose = 'sign';
      const fileIndex = this.pendingFirmaVisibleFileIndex;
      this.pendingFirmaVisibleFileIndex = null;
      if (fileIndex != null) {
        const editable = this.isVisibleSignaturePlaceholder(cn);
        this.openFirmaVisibleModalInternal(fileIndex, editable ? '' : cn, {
          signerCnEditable: editable
        });
      }
      return;
    }

    this.uiHelper.showSpinner('Firmando documentos con certificado P10...', 'sign');
    
    const state = this.signStateService.currentState;
    if (state.fileType === 'other') {
      this.signAllP10Cades();
    } else if (state.fileType === 'pdf') {
      this.signAllP10Pdf();
    } else {
      this.uiHelper.hideSpinner();
    }
  }

  private getSignProgressMessage(current: number, total: number, fileName?: string): string {
    const name = fileName?.trim();
    if (total <= 1) {
      return name ? `Firmando ${name}...` : 'Firmando documento...';
    }
    return name
      ? `Firmando ${current} de ${total}: ${name}`
      : `Firmando documento ${current} de ${total}...`;
  }

  private beginAuditSession(): string {
    this.auditTransactionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tx-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.auditSerial = null;
    this.auditIdentificationNumber = null;
    return this.auditTransactionId;
  }

  private captureAuditFromResponse(response?: {
    transactionId?: string | null;
    serial?: string | null;
    identificationNumber?: string | null;
  } | null): void {
    if (response?.transactionId) {
      this.auditTransactionId = response.transactionId;
    }
    if (response?.serial) {
      this.auditSerial = response.serial;
    }
    if (response?.identificationNumber) {
      this.auditIdentificationNumber = response.identificationNumber;
    }
  }

  /** Toma el documento enriquecido desde Sign_RSE tras el callback (si RSE lo expuso). */
  private captureAuditFromRseProcess(process?: SignRseProcessResponse | null): void {
    const id = (process?.identificationNumber || '').trim();
    if (id && id.toUpperCase() !== 'PENDING_RSE') {
      this.auditIdentificationNumber = id;
    }
  }

  private resolveRseSignedDisplayName(
    originalFileName: string,
    process?: SignRseProcessResponse | null,
    contentDispositionFileName?: string | null
  ): string {
    const defaultCadesExt = normalizeCadesOutputExtension(
      (environment as { signRseCadesOutputExtension?: string }).signRseCadesOutputExtension
    );
    return resolveRseSignedFileName(
      originalFileName,
      process,
      contentDispositionFileName,
      defaultCadesExt
    );
  }

  private setSignProgress(current: number, total: number, fileName?: string, file?: File): void {
    const stateFile = file || this.signStateService.currentState.files[current - 1];
    const name = fileName || stateFile?.name || undefined;
    this.uiHelper.showSignJourney({
      phase: 'upload',
      current,
      total,
      fileName: name,
      fileSizeBytes: stateFile?.size ?? null,
      mensaje: this.getSignProgressMessage(current, total, name)
    });
  }

  private isAuthSignError(error: any): boolean {
    const status = error?.status;
    const msg = (error?.error?.message || error?.message || '').toLowerCase();
    return status === 401 || status === 403
      || msg.includes('password')
      || msg.includes('credencial')
      || msg.includes('invalid');
  }

  private resolveSignHttpErrorMessage(error: any): string {
    const errorStatus = error?.status;
    const errorResponse = error?.error ?? error?.originalError?.error;
    if (errorStatus === 400 && errorResponse?.message) {
      return errorResponse.message;
    }
    if (this.isAuthSignError(error)) {
      return FILE_ERROR_MESSAGES.INVALID_CREDENTIALS as string;
    }
    if (errorResponse?.message) {
      return errorResponse.message;
    }
    if (error?.message) {
      return error.message;
    }
    return FILE_ERROR_MESSAGES.SIGN_ERROR as string;
  }

  private finishSequentialSign(opts: {
    signedSrcs: string[];
    signedNames: string[];
    failedMessages: string[];
    fileType: 'pdf' | 'other';
  }): void {
    this.uiHelper.unlockUi();
    this.notificationService.cleanupSwal();
    const { signedSrcs, signedNames, failedMessages, fileType } = opts;

    // Cancelación explícita del usuario en el modal RSE: salir sin error rojo.
    const onlyUserCancel =
      !signedSrcs.length &&
      failedMessages.length > 0 &&
      failedMessages.every((m) =>
        /autenticaci[oó]n con c[oó]digo de verificaci[oó]n cancelad/i.test(m)
      );
    if (onlyUserCancel) {
      this.uiHelper.hideSpinner();
      return;
    }

    if (signedSrcs.length) {
      this.signStateService.setSignedFiles(signedSrcs);
      this.signStateService.setShowPdfViewer(false);
      // Quitar viaje/spinner justo al presentar el modal de éxito.
      this.uiHelper.hideSpinner();
      this.openSignedFilesModal(fileType, signedNames);
    }

    if (signedSrcs.length && failedMessages.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Firma parcial',
        text: failedMessages.filter(Boolean).join(' ') || FILE_ERROR_MESSAGES.SIGN_FAILED,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    if (!signedSrcs.length) {
      this.uiHelper.hideSpinner();
      if (this.signCertificateType === 'p10') {
        this.signStateService.clearUploadedCertificate();
      }
      const text = failedMessages.filter(Boolean).join(' ') || FILE_ERROR_MESSAGES.SIGN_FAILED;
      const canceled = /cancelad/i.test(text);
      void Swal.fire({
        icon: canceled ? 'info' : 'error',
        title: canceled ? 'Firma cancelada' : 'No se pudo firmar',
        text,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      }).then(() => {
        this.notificationService.cleanupSwal();
      });
    }
  }

  private showSignFatalError(error: any): void {
    this.uiHelper.unlockUi();
    // Contraseña / credenciales P10 inválidas: permitir elegir de nuevo.
    if (this.signCertificateType === 'p10' && this.isAuthSignError(error)) {
      this.signStateService.clearUploadedCertificate();
    }
    const message = this.resolveSignHttpErrorMessage(error);
    if (/cancelad/i.test(message)) {
      this.notificationService.cleanupSwal();
      return;
    }
    void Swal.fire({
      icon: 'error',
      title: 'Error',
      text: message,
      confirmButtonText: 'Entendido',
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        confirmButton: 'custom-swal-button'
      },
      ...this.notificationService.getSwalBaseConfig()
    }).then(() => {
      this.notificationService.cleanupSwal();
    });
  }

  signAllCades(): void {
    const state = this.signStateService.currentState;
    if (!(state.selectedCertificate && state.files.length && state.user && state.password)) {
      this.uiHelper.hideSpinner();
      if (state.selectedCertificate && state.user && state.password && !state.files.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Sin documentos',
          text: 'No hay archivos listos para firmar. Vuelve a cargar el documento en la sección de firmar.',
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
      }
      return;
    }

    const files = state.files;
    const total = files.length;
    const fileNames = files.map((f, i) => f.name || `documento_${i + 1}`);
    const transactionId = this.beginAuditSession();
    this.auditSerial = state.selectedCertificate!.serial;
    this.auditIdentificationNumber = state.user;

    this.setSignProgress(1, total, fileNames[0], files[0]);

    from(files.map((file, index) => ({ file, index }))).pipe(
      concatMap(({ file, index }) => {
        this.setSignProgress(index + 1, total, fileNames[index], file);
        return this.signService.signCadesTokenVirtualMultipart({
          document: file,
          fileName: fileNames[index],
          nuip: state.user!,
          password: state.password!,
          serial: state.selectedCertificate!.serial,
          serialNumber: state.selectedCertificate!.serialNumber,
          issuer: state.selectedCertificate!.issuer,
          attached: true,
          timestampEnabled: this.resolveSignTsaLtvFlags().timestampEnabled,
          transactionId
        }).pipe(
          map(result => {
            this.captureAuditFromMultipart(result);
            this.uiHelper.updateSignJourney({
              phase: 'prepare',
              current: index + 1,
              total,
              fileName: fileNames[index],
              fileSizeBytes: file.size
            });
            return { ok: true as const, result, index };
          }),
          catchError(error => {
            if (this.isAuthSignError(error)) {
              return throwError(() => error);
            }
            return of({ ok: false as const, error, index });
          })
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (outcomes) => {
        void this.finishSequentialSignFromMultipart({
          outcomes,
          fileNames,
          fileType: 'other',
          mimeForDataUrl: 'application/octet-stream'
        });
      },
      error: (error) => this.showSignFatalError(error)
    });
  }

  signAllP10Cades(): void {
    const state = this.signStateService.currentState;
    if (!state.uploadedCertificate || !state.files.length || !state.uploadedCertificatePassword) {
      this.uiHelper.hideSpinner();
      return;
    }

    const files = state.files;
    if (!files.length) {
      this.uiHelper.hideSpinner();
      Swal.fire({
        icon: 'warning',
        title: 'Documentos inválidos',
        text: 'No se encontraron documentos válidos para firmar como archivo .p7z.',
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    const total = files.length;
    const fileNames = files.map((f, i) => f.name || `documento_${i + 1}`);
    const transactionId = this.beginAuditSession();
    const pfxBlob = this.base64ToBlob(state.uploadedCertificate!, 'application/x-pkcs12');
    this.setSignProgress(1, total, fileNames[0], files[0]);

    from(files.map((file, index) => ({ file, index }))).pipe(
      concatMap(({ file, index }) => {
        this.setSignProgress(index + 1, total, fileNames[index], file);
        return this.signService.signCadesPfxMultipart({
          document: file,
          fileName: fileNames[index],
          pfx: pfxBlob,
          pfxPassword: state.uploadedCertificatePassword!,
          attached: true,
          timestampEnabled: this.resolveSignTsaLtvFlags().timestampEnabled,
          transactionId
        }).pipe(
          map(result => {
            this.captureAuditFromMultipart(result);
            this.uiHelper.updateSignJourney({
              phase: 'prepare',
              current: index + 1,
              total,
              fileName: fileNames[index],
              fileSizeBytes: file.size
            });
            return { ok: true as const, result, index };
          }),
          catchError(error => {
            if (this.isAuthSignError(error)) {
              return throwError(() => error);
            }
            return of({ ok: false as const, error, index });
          })
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (outcomes) => {
        void this.finishSequentialSignFromMultipart({
          outcomes,
          fileNames,
          fileType: 'other',
          mimeForDataUrl: 'application/octet-stream'
        });
      },
      error: (error) => this.showSignFatalError(error)
    });
  }

  signAllPdfs(): void {
    const state = this.signStateService.currentState;
    if (!state.selectedCertificate || !state.files.length || !state.user || !state.password) {
      this.uiHelper.hideSpinner();
      if (state.selectedCertificate && state.user && state.password && !state.files.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Sin documentos',
          text: 'No hay archivos listos para firmar. Vuelve a cargar el documento en la sección de firmar.',
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
      }
      return;
    }

    const files = state.files;
    const total = files.length;
    const fileNames = files.map((f, i) => f.name || `documento_${i + 1}`);
    const transactionId = this.beginAuditSession();
    this.auditSerial = state.selectedCertificate!.serial;
    this.auditIdentificationNumber = state.user;
    this.setSignProgress(1, total, fileNames[0], files[0]);

    from(files.map((file, index) => ({ file, index }))).pipe(
      concatMap(({ file, index }) => {
        this.setSignProgress(index + 1, total, fileNames[index], file);
        return from(this.buildPdfMultipartAppearanceParams(index)).pipe(
          concatMap((appearance) => this.signService.signPdfTokenVirtualMultipart({
            document: file,
            fileName: fileNames[index],
            nuip: state.user!,
            password: state.password!,
            serial: state.selectedCertificate!.serial,
            serialNumber: state.selectedCertificate!.serialNumber,
            issuer: state.selectedCertificate!.issuer,
            signReason: 'Firmado Digitalmente',
            signLocation: 'Servidor Colombia',
            transactionId,
            ...appearance
          }).pipe(
            map(result => {
              this.captureAuditFromMultipart(result);
              this.uiHelper.updateSignJourney({
                phase: 'prepare',
                current: index + 1,
                total,
                fileName: fileNames[index],
                fileSizeBytes: file.size
              });
              return { ok: true as const, result, index };
            }),
            catchError(error => {
              if (this.isAuthSignError(error)) {
                return throwError(() => error);
              }
              return of({ ok: false as const, error, index });
            })
          ))
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (outcomes) => {
        void this.finishSequentialSignFromMultipart({
          outcomes,
          fileNames,
          fileType: 'pdf',
          mimeForDataUrl: 'application/pdf'
        });
      },
      error: (error) => this.showSignFatalError(error)
    });
  }

  private buildPdfVisibleAppearance(
    zone: PadesSignatureZoneResult,
    imageChoice: PadesSignatureImageChoice,
    certificateCn: string
  ): SignPdfAppearanceEnabled {
    const cn = certificateCn.trim() || this.visibleSignatureFallbackText;
    const stampImage = imageChoice.imageBase64?.trim();
    const contentSignature = buildAdobeStampDescription(cn);

    if (stampImage) {
      return {
        enabled: true,
        mode: 'GRAPHIC',
        signFieldName: this.generateUniqueSignFieldName(),
        rect: {
          llx: zone.lowerLeftX,
          lly: zone.lowerLeftY,
          urx: zone.upperRightX,
          ury: zone.upperRightY
        },
        page: zone.signPage,
        contentSignature,
        imageBase64: stampImage,
        imageValidation: true
      };
    }

    return {
      enabled: true,
      mode: 'DESCRIPTION',
      signFieldName: this.generateUniqueSignFieldName(),
      rect: {
        llx: zone.lowerLeftX,
        lly: zone.lowerLeftY,
        urx: zone.upperRightX,
        ury: zone.upperRightY
      },
      page: zone.signPage,
      contentSignature,
      imageBase64: TRANSPARENT_1X1_PNG_BASE64,
      imageValidation: true
    };
  }

  signAllP10Pdf(): void {
    const state = this.signStateService.currentState;
    if (!state.uploadedCertificate || !state.files.length || !state.uploadedCertificatePassword) {
      this.uiHelper.hideSpinner();
      if (
        state.uploadedCertificate &&
        state.uploadedCertificatePassword &&
        !state.files.length
      ) {
        Swal.fire({
          icon: 'warning',
          title: 'Sin documentos',
          text: 'No hay archivos listos para firmar. Vuelve a cargar el documento en la sección de firmar.',
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
      }
      return;
    }

    const files = state.files;
    const total = files.length;
    const fileNames = files.map((f, i) => f.name || `documento_${i + 1}`);
    const transactionId = this.beginAuditSession();
    const pfxBlob = this.base64ToBlob(state.uploadedCertificate!, 'application/x-pkcs12');
    this.setSignProgress(1, total, fileNames[0], files[0]);

    from(files.map((file, index) => ({ file, index }))).pipe(
      concatMap(({ file, index }) => {
        this.setSignProgress(index + 1, total, fileNames[index], file);
        return from(this.buildPdfMultipartAppearanceParams(index)).pipe(
          concatMap((appearance) => this.signService.signPdfPfxMultipart({
            document: file,
            fileName: fileNames[index],
            pfx: pfxBlob,
            pfxPassword: state.uploadedCertificatePassword!,
            signReason: 'Firma Digital',
            signLocation: 'Servidores Colombia',
            transactionId,
            ...appearance
          }).pipe(
            map(result => {
              this.captureAuditFromMultipart(result);
              this.uiHelper.updateSignJourney({
                phase: 'prepare',
                current: index + 1,
                total,
                fileName: fileNames[index],
                fileSizeBytes: file.size
              });
              return { ok: true as const, result, index };
            }),
            catchError(error => {
              if (this.isAuthSignError(error)) {
                return throwError(() => error);
              }
              return of({ ok: false as const, error, index });
            })
          ))
        );
      }),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (outcomes) => {
        void this.finishSequentialSignFromMultipart({
          outcomes,
          fileNames,
          fileType: 'pdf',
          mimeForDataUrl: 'application/pdf'
        });
      },
      error: (error) => this.showSignFatalError(error)
    });
  }

  cancelSign(): void {
    this.resetSignState();
  }

  resetSignState(): void {
    this.resetVisibleSignatureState();
    this.signStateService.resetSignSection();
    this.resetSignFileInputs();
  }

  /** Limpia opciones de firma visible (zona, sello/imagen). */
  resetVisibleSignatureState(): void {
    this.pdfWantVisibleSignature = [];
    this.pdfVisibleSignatureConfigs = [];
    this.pdfVisibleFileKeys = [];
    this.visibleSignerCn = null;
    this.authPurpose = 'sign';
    this.pendingFirmaVisibleFileIndex = null;
  }

  private isVisibleSignaturePlaceholder(value: string | null | undefined): boolean {
    const normalized = (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return !normalized || this.visibleSignaturePlaceholders.has(normalized);
  }

  private sanitizeVisibleSignerCn(value: string | null | undefined): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed || this.isVisibleSignaturePlaceholder(trimmed)) {
      return null;
    }
    return trimmed;
  }

  private getVisibleSignatureTextForEntry(entry: {
    visibleSignature: PadesVisibleSignatureConfig;
  }): string {
    const state = this.signStateService.currentState;
    // Preferir CN del certificado autenticado/cargado sobre texto guardado en el modal
    return (
      this.sanitizeVisibleSignerCn(state.selectedCertificate?.cn)
      || this.sanitizeVisibleSignerCn(this.visibleSignerCn)
      || this.sanitizeVisibleSignerCn(entry.visibleSignature?.text)
      || this.visibleSignatureFallbackText
    );
  }

  /** CN del certificado ya autenticado/cargado, si existe. */
  private resolveVisibleSignerCn(): string | null {
    const fromState = this.sanitizeVisibleSignerCn(this.visibleSignerCn);
    if (fromState) {
      return fromState;
    }
    const state = this.signStateService.currentState;
    if (state.certificateType === 'token') {
      return this.sanitizeVisibleSignerCn(state.selectedCertificate?.cn);
    }
    return null;
  }

  private cnFromLocalAgentCertificate(cert: LocalAgentCertificate | undefined | null): string | null {
    if (!cert) return null;
    return this.sanitizeVisibleSignerCn(dnCommonName(cert.subjectDN))
      || this.sanitizeVisibleSignerCn(cert.friendlyName);
  }

  private ensurePdfBase64sThenSign(signFn: () => void): void {
    const state = this.signStateService.currentState;
    const isPdf = this.signFileType === 'pdf';
    const base64s = isPdf ? state.pdfBase64s : state.cadesBase64s;
    const hasBase64 = base64s.length === state.files.length && state.files.length > 0;
    if (hasBase64) {
      signFn();
      return;
    }
    if (!this.signFiles.length) {
      this.uiHelper.hideSpinner();
      this.rseActiveAuthDialog?.close({ status: 'cancel' });
      this.clearRseActiveAuthDialog();
      this.closePendingRseAuthPopup();
      return;
    }
    this.readFilesAsBase64(this.signFiles).then(
      (base64List) => {
        if (isPdf) {
          this.signStateService.setPdfBase64s(base64List);
        } else {
          this.signStateService.setCadesBase64s(base64List);
        }
        signFn();
      },
      () => {
        this.uiHelper.hideSpinner();
        this.rseActiveAuthDialog?.close({ status: 'cancel' });
        this.clearRseActiveAuthDialog();
        this.closePendingRseAuthPopup();
        this.openAlertModal('error', 'Error', 'No se pudieron leer los archivos.');
      }
    );
  }

  /**
   * Genera un signFieldName único por firma (evita fallos de cofirma en PDF).
   * Formato: sig_ + 10 caracteres alfanuméricos (solo a-z, 0-9).
   */
  private generateUniqueSignFieldName(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'sig_';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /** Abre el modal de zona de firma: autentica/carga certificado primero para obtener el CN. */
  openFirmaVisibleModalForSinglePdf(fileIndex: number): void {
    if (
      fileIndex < 0 ||
      fileIndex >= this.signFiles.length ||
      !this.pdfWantVisibleSignature[fileIndex]
    ) {
      return;
    }

    this.pendingFirmaVisibleFileIndex = fileIndex;
    const cn = this.resolveVisibleSignerCn();
    if (cn) {
      this.openFirmaVisibleModalInternal(fileIndex, cn);
      return;
    }

    // RSE / Certifirma Digital Físico: el CN del certificado aún no está en Certitool → campo editable.
    if (this.signMechanism === 'rse' || this.signMechanism === 'token-fisico') {
      this.openFirmaVisibleModalInternal(fileIndex, '', { signerCnEditable: true });
      return;
    }

    this.authPurpose = 'firmaVisible';
    if (this.signMechanism === 'p10' || this.signCertificateType === 'p10') {
      this.openPkcs12UploadModal();
    } else {
      this.openAuthModal();
    }
  }

  private openFirmaVisibleModalInternal(
    fileIndex: number,
    signerCn: string,
    options?: { signerCnEditable?: boolean }
  ): void {
    if (
      fileIndex < 0 ||
      fileIndex >= this.signFiles.length ||
      !this.pdfWantVisibleSignature[fileIndex]
    ) {
      return;
    }

    const editable = !!options?.signerCnEditable;
    const sanitized = this.sanitizeVisibleSignerCn(signerCn);
    const cn = sanitized || (editable ? '' : this.visibleSignatureFallbackText);
    if (sanitized) {
      this.visibleSignerCn = sanitized;
    }
    const f = this.signFiles[fileIndex];
    this.readFilesAsBase64([f]).then(
      (base64List) => {
        const pdfSrc = `data:application/pdf;base64,${base64List[0]}`;
        const existing = this.pdfVisibleSignatureConfigs[fileIndex];
        const ref = this.dialog.open(PadesSignatureZoneModalComponent, {
          width: '75vw',
          maxWidth: '900px',
          height: '85vh',
          maxHeight: '85vh',
          panelClass: ['custom-dialog', 'pades-zone-dialog'],
          data: {
            pdfSrc,
            documentLabel: f.name,
            signerCn: cn,
            signerCnEditable: editable,
            initialConfig: existing
              ? {
                  zone: existing.zone,
                  imageChoice: existing.imageChoice,
                  visibleSignature: existing.visibleSignature
                }
              : undefined
          }
        });
        ref
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((result: PadesSignatureZoneModalResult | undefined) => {
            if (result?.zone && result?.imageChoice && result.visibleSignature) {
              const resultCn = this.sanitizeVisibleSignerCn(result.visibleSignature.text);
              if (resultCn) {
                this.visibleSignerCn = resultCn;
              }
              this.pdfVisibleSignatureConfigs[fileIndex] = {
                zone: result.zone,
                imageChoice: result.imageChoice,
                visibleSignature: {
                  ...result.visibleSignature,
                  text: resultCn || result.visibleSignature.text
                }
              };
            }
          });
      },
      () => {}
    );
  }

  // --- Verify Section Logic ---
  onVerifyFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) {
      return;
    }

    const file = input.files[0];

    // Validar tamaño
    if (file.size > FILE_SIZE.MAX_SIZE_OTHER) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo demasiado grande',
        text: FILE_ERROR_MESSAGES.FILE_TOO_LARGE,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      input.value = '';
      return;
    }

    // Validar archivo vacío
    if (file.size < FILE_LIMITS.MIN_FILE_SIZE) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo inválido',
        text: FILE_ERROR_MESSAGES.FILE_TOO_SMALL,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      input.value = '';
      return;
    }

    if (!this.isValidVerifyFile(file)) {
      input.value = '';
      return;
    }

    this.verifyStateService.setFile(file);
  }

  onVerifyDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!event.dataTransfer?.files || !event.dataTransfer.files[0]) {
      return;
    }

    const file = event.dataTransfer.files[0];

    if (!this.isValidVerifyFile(file)) {
      return;
    }

    this.verifyStateService.setFile(file);
  }

  /** Valida tamaño y extensión para verificación (.pdf, .p7z, .p7m, .xml). */
  private isValidVerifyFile(file: File): boolean {
    if (file.size > FILE_SIZE.MAX_SIZE_OTHER) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo demasiado grande',
        text: FILE_ERROR_MESSAGES.FILE_TOO_LARGE,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return false;
    }

    if (file.size < FILE_LIMITS.MIN_FILE_SIZE) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo inválido',
        text: FILE_ERROR_MESSAGES.FILE_TOO_SMALL,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return false;
    }

    const fileName = file.name.toLowerCase();
    const isValidExtension =
      fileName.endsWith(FILE_EXTENSIONS.PDF) ||
      fileName.endsWith(FILE_EXTENSIONS.P7Z) ||
      fileName.endsWith(FILE_EXTENSIONS.P7M) ||
      fileName.endsWith(FILE_EXTENSIONS.XML);

    let isValidMimeType = true;
    if (fileName.endsWith(FILE_EXTENSIONS.PDF) && file.type !== '') {
      isValidMimeType = ALLOWED_FILE_TYPES.PDF.mimeTypes.includes(file.type as 'application/pdf');
    } else if (fileName.endsWith(FILE_EXTENSIONS.XML) && file.type !== '') {
      isValidMimeType = (ALLOWED_FILE_TYPES.XADES.mimeTypes as readonly string[]).includes(file.type);
    }

    if (!isValidExtension || !isValidMimeType) {
      Swal.fire({
        icon: 'error',
        title: 'Tipo de archivo inválido',
        text: FILE_ERROR_MESSAGES.INVALID_VERIFY_TYPE,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return false;
    }

    return true;
  }

  /** Mueve el foco a la barra de resultado para teclado y lectores de pantalla tras verificar. */
  private focusVerifyResultRegion(): void {
    setTimeout(() => {
      document.getElementById('verify-result-region')?.focus({ preventScroll: true });
    }, 0);
  }

  verifySignature(): void {
    const state = this.verifyStateService.currentState;
    if (!state.file) {
      this.openAlertModal('warning', 'Advertencia', FILE_ERROR_MESSAGES.VERIFY_NO_FILE);
      return;
    }

    this.uiHelper.showSpinner('Verificando firmas del documento...', 'verify');
    this.verifyStateService.setProcessing(true);

    // Llama a POST /api/verify/document/multipart (document + fileName)
    const verifyObservable = this.verifyService.verifySignature(state.file);

    verifyObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (response: VerifySignatureResponse) => {
        this.uiHelper.hideSpinner();
        this.verifyStateService.setProcessing(false);

        const results = Array.isArray(response.results) ? response.results : [];
        const successfulResults = results.filter(r => r.success);
        const failedResults = results.filter(r => !r.success);
        this.verifyLastCheckDate = new Date();

        if (response.success && successfulResults.length > 0) {
          const firmas = successfulResults.map(result => this.mapVerifyResultToCertificateDetail(result));
          this.verifyStateService.setSignatures(firmas);
          const primeraFirma = firmas[0];
          this.verifyStateService.setVerificationResult({
            valid: true,
            signer: primeraFirma.titular,
            date: new Date(primeraFirma.fechaFirma),
            certificate: primeraFirma.entidad,
            firmas
          });
          if (this.isVerifyFilePdf) {
            // PDF: panel abierto, firmas colapsadas (sin selección)
            this.verifyPanelOpen = true;
            this.verifyStateService.selectSignature(null, null);
          } else {
            // CAdES: seleccionar primera firma para el panel de detalle
            this.verifyStateService.selectSignature(primeraFirma, 0);
          }
          this.focusVerifyResultRegion();
        } else if (failedResults.length > 0 || !response.success) {
          const firmas = (failedResults.length > 0 ? failedResults : results)
            .map(result => this.mapVerifyResultToCertificateDetail(result));
          const hasSignerData = firmas.some(f => f.titular && f.titular !== 'N/A');
          this.verifyStateService.setSignatures(hasSignerData ? firmas : []);
          const errors = (failedResults.length > 0 ? failedResults : results).map(result => ({
            code: result.code ?? 'APP-VERIFY-003',
            message: result.message ?? response.message ?? FILE_ERROR_MESSAGES.VERIFY_FAILED,
            serviceMessage: result.serviceMessage ?? null
          }));
          const primera = firmas[0];
          this.verifyStateService.setVerificationResult({
            valid: false,
            signer: primera?.titular ?? 'N/A',
            date: new Date(),
            certificate: primera?.entidad ?? 'N/A',
            firmas: hasSignerData ? firmas : undefined,
            errores: errors
          });
          if (hasSignerData && primera) {
            if (this.isVerifyFilePdf) {
              this.verifyPanelOpen = true;
              this.verifyStateService.selectSignature(null, null);
            } else {
              this.verifyStateService.selectSignature(primera, 0);
            }
          }
          const detail =
            errors[0]?.serviceMessage
            || errors[0]?.message
            || FILE_ERROR_MESSAGES.VERIFY_FAILED;
          this.openAlertModal('error', 'Firma Inválida', detail);
          this.focusVerifyResultRegion();
        } else {
          this.certificateList = [];
          this.selectedDetail = null;
          this.selectedRowIndex = null;
          this.openAlertModal('warning', 'Sin Resultados', FILE_ERROR_MESSAGES.VERIFY_NO_SIGNATURES);
        }
      },
      error: (error) => {
        this.uiHelper.hideSpinner();
        this.verifyStateService.setProcessing(false);
        console.error('Error al verificar firma:', error);
        const backendMessage = error?.error?.message;
        const errorMessage = error?.status === 400 && backendMessage
          ? backendMessage
          : FILE_ERROR_MESSAGES.VERIFY_FAILED;
        this.openAlertModal('error', 'Error de Verificación', errorMessage);
      }
    });
  }

  /** Abre el modal de error/advertencia responsive (mismo que Certifirma Digital Físico). */
  private openAlertModal(type: TokenFisicoAlertType, title: string, message: string): void {
    this.dialog.open(TokenFisicoAlertModalComponent, {
      width: '440px',
      maxWidth: '95vw',
      disableClose: false,
      panelClass: ['custom-dialog', 'verify-dialog-responsive'],
      data: { type, title, message } as TokenFisicoAlertModalData
    });
  }

  private mapVerifyResultToCertificateDetail(result: VerifyDocumentResultItem): CertificateDetail {
    return {
      asunto: result.subject ?? 'N/A',
      titular: result.signerName ?? 'N/A',
      entidad: result.issuer ?? 'N/A',
      serial: result.serial ?? 'N/A',
      validoDesde: result.validFrom ?? 'N/A',
      validoHasta: result.validTo ?? 'N/A',
      emisor: result.issuer ?? 'N/A',
      fechaFirma: result.signDate ?? result.timestampDate ?? new Date().toISOString(),
      timestamped: result.timestamped ?? null,
      timestampDate: result.timestampDate ?? null,
      ltvEnabled: result.ltvEnabled ?? null,
      code: result.code ?? null,
      serviceMessage: result.serviceMessage ?? null,
      success: result.success
    };
  }

  /** Texto TSA para panel/modales según respuesta del BFF. */
  getTimestampDetailText(cert: CertificateDetail | null | undefined): string {
    if (cert?.timestamped) {
      const when = cert.timestampDate ? this.formatVerifyDate(cert.timestampDate) : null;
      return when
        ? `La hora de la firma procede de una estampa de tiempo confiable (${when}).`
        : 'La hora de la firma procede de una estampa de tiempo confiable (TSA).';
    }
    return 'La hora de la firma procede del reloj del equipo del firmante.';
  }

  /** Texto LTV para panel/modales según respuesta del BFF. */
  getLtvDetailText(cert: CertificateDetail | null | undefined): string {
    if (cert?.ltvEnabled === true) {
      return 'La firma incluye evidencia LTV embebida en el documento.';
    }
    if (cert?.ltvEnabled === null || cert?.ltvEnabled === undefined) {
      return 'LTV no aplica para este tipo de firma.';
    }
    return `La firma no está activada para LTV y caducará después de ${this.formatValidoHastaForLTV(cert?.validoHasta)}.`;
  }

  getVerifyTypeLabel(): string {
    const fileName = this.verifyFile?.name?.toLowerCase() ?? '';
    if (fileName.endsWith(FILE_EXTENSIONS.PDF)) return 'Tipo: PDF';
    if (fileName.endsWith(FILE_EXTENSIONS.P7M)) return 'Tipo: Archivo .p7m';
    if (fileName.endsWith(FILE_EXTENSIONS.P7Z)) return 'Tipo: Archivo .p7z';
    if (fileName.endsWith(FILE_EXTENSIONS.XML)) return 'Tipo: Archivo .xml (XAdES)';
    return 'Tipo: Desconocido';
  }

  getVerifyTypeDescription(): string {
    const fileName = this.verifyFile?.name?.toLowerCase() ?? '';
    if (fileName.endsWith(FILE_EXTENSIONS.PDF)) {
      return 'PDF: la firma está integrada dentro del documento.';
    }
    if (fileName.endsWith(FILE_EXTENSIONS.P7M)) {
      return '.p7m: contenedor CAdES con el documento embebido.';
    }
    if (fileName.endsWith(FILE_EXTENSIONS.P7Z)) {
      return '.p7z: archivo de firma CAdES (adjunto o separado).';
    }
    if (fileName.endsWith(FILE_EXTENSIONS.XML)) {
      return '.xml: firma XAdES Enveloped dentro del documento XML.';
    }
    return 'Tipo de archivo no identificado para verificación.';
  }

  formatVerifyDate(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return 'N/A';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return String(dateValue);
    // Evitar años absurdos por datos mal formados del backend
    if (date.getFullYear() < 1990 || date.getFullYear() > 2100) {
      return String(dateValue);
    }
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /** Resumen legible de un DN (CN · O) para el panel CAdES. */
  formatDnSummary(dn: string | null | undefined): string {
    if (!dn || dn === 'N/A') return 'N/A';
    const cn = dnCommonName(dn);
    const org = parseDnAttributes(dn).find((a) => a.key.toUpperCase() === 'O')?.value;
    if (cn && org && org !== cn) return `${cn} · ${org}`;
    if (cn) return cn;
    return dn.length > 120 ? `${dn.slice(0, 117)}…` : dn;
  }

  /** Formato "Última comprobación" tipo Adobe: 2026.03.13 18:50:29 */
  formatLastCheckDate(date: Date | null | undefined): string {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const tz = -d.getTimezoneOffset();
    const tzSign = tz >= 0 ? '+' : '-';
    const tzMin = Math.abs(tz) % 60;
    const tzHour = Math.floor(Math.abs(tz) / 60);
    const tzStr = `${tzSign}${String(tzHour).padStart(2, '0')}'${String(tzMin).padStart(2, '0')}'`;
    return `${y}.${m}.${day} ${h}:${min}:${s} ${tzStr}`;
  }

  /** Formato "caducará después de" para LTV: 2027/03/13 */
  formatValidoHastaForLTV(validoHasta: string | null | undefined): string {
    if (!validoHasta || validoHasta === 'N/A') return '—';
    const d = new Date(validoHasta);
    if (Number.isNaN(d.getTime())) return validoHasta;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
  }

  // --- Verify Section Additional Methods ---
  selectDetail(cert: CertificateDetail, index: number): void {
    const state = this.verifyStateService.currentState;
    // Segundo clic en la misma firma: colapsar (estilo panel Adobe / PDF)
    if (state.selectedIndex === index) {
      this.verifyStateService.selectSignature(null, null);
      return;
    }
    this.verifyStateService.selectSignature(cert, index);
    if (state.verificationResult) {
      this.verifyStateService.setVerificationResult({
        ...state.verificationResult,
        signer: cert.titular,
        date: new Date(cert.fechaFirma),
        certificate: cert.entidad
      });
    }
  }

  /** Selección en tabla CAdES (no colapsa; siempre deja una fila activa). */
  selectCadesFirma(cert: CertificateDetail, index: number): void {
    const state = this.verifyStateService.currentState;
    this.verifyStateService.selectSignature(cert, index);
    if (state.verificationResult) {
      this.verifyStateService.setVerificationResult({
        ...state.verificationResult,
        signer: cert.titular,
        date: new Date(cert.fechaFirma),
        certificate: cert.entidad
      });
    }
  }

  resetVerify(): void {
    if (this.verifyFileObjectUrl) {
      URL.revokeObjectURL(this.verifyFileObjectUrl);
      this.verifyFileObjectUrl = null;
    }
    this.verifyLastCheckDate = null;
    this.verifyStateService.reset();
    this.verifyPanelOpen = true;
    this.closeVerifyContextMenu();
    // Resetear el input file si existe
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  openCertificate(): void {
    const state = this.verifyStateService.currentState;
    if (state.selectedIndex !== null && state.signatures[state.selectedIndex]) {
      this.openCertificateFor(state.signatures[state.selectedIndex], state.selectedIndex);
    }
  }

  /** Abre propiedades de una firma concreta (CAdES en tarjeta o panel PDF). */
  openCertificateFor(cert: CertificateDetail, index: number): void {
    this.verifyStateService.selectSignature(cert, index);
    this.closeVerifyContextMenu();
    this.dialog.open(CertificateInfoModalComponent, {
      data: { cert, lastCheckDate: this.verifyLastCheckDate },
      width: '480px',
      maxWidth: '100vw',
      panelClass: ['custom-dialog', 'verify-dialog-responsive']
    });
  }

  /** Alterna visibilidad del panel lateral "Firmas" (Propuesta 1) */
  toggleVerifyPanel(): void {
    this.verifyPanelOpen = !this.verifyPanelOpen;
  }

  /** Cierra el menú contextual de la zona de firma */
  closeVerifyContextMenu(): void {
    this.verifyContextMenuVisible = false;
    this.verifyContextMenuCert = null;
    this.verifyContextMenuIndex = -1;
  }

  /** Abre el menú de la firma con clic normal sobre la zona. */
  onSignatureZoneClick(event: Event, cert: CertificateDetail, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectDetail(cert, index);
    this.verifyPanelOpen = true;

    const mouse = event as MouseEvent;
    const target = event.currentTarget as HTMLElement | null;
    if (typeof mouse.clientX === 'number' && mouse.clientX > 0) {
      this.verifyContextMenuX = mouse.clientX;
      this.verifyContextMenuY = mouse.clientY;
    } else if (target) {
      const rect = target.getBoundingClientRect();
      this.verifyContextMenuX = Math.round(rect.left + rect.width / 2);
      this.verifyContextMenuY = Math.round(rect.bottom + 4);
    } else {
      this.verifyContextMenuX = 24;
      this.verifyContextMenuY = 24;
    }

    this.verifyContextMenuCert = cert;
    this.verifyContextMenuIndex = index;
    this.verifyContextMenuVisible = true;
  }

  /** Abre el modal "Estado de validación de la firma" (desde menú contextual o panel) */
  openSignatureValidationModal(cert: CertificateDetail): void {
    this.closeVerifyContextMenu();
    const valid = cert.success !== false && (this.verificationResult?.valid ?? false);
    const ref = this.dialog.open(SignatureValidationModalComponent, {
      data: {
        valid,
        signerName: cert.titular,
        cert,
        lastCheckDate: this.verifyLastCheckDate
      } as SignatureValidationModalData,
      width: '560px',
      maxWidth: '96vw',
      autoFocus: 'dialog',
      restoreFocus: true,
      panelClass: ['custom-dialog', 'verify-dialog-responsive']
    });
    ref.afterClosed().subscribe((result?: { openProperties?: boolean; cert?: CertificateDetail }) => {
      if (result?.openProperties && result.cert) {
        this.dialog.open(CertificateInfoModalComponent, {
          data: { cert: result.cert, lastCheckDate: this.verifyLastCheckDate },
          width: '480px',
          maxWidth: '100vw',
          panelClass: ['custom-dialog', 'verify-dialog-responsive']
        });
      }
    });
  }

  /** Opción "Propiedades de la firma" del menú contextual */
  openSignaturePropertiesFromContext(): void {
    const cert = this.verifyContextMenuCert;
    this.closeVerifyContextMenu();
    if (cert) {
      this.dialog.open(CertificateInfoModalComponent, {
        data: { cert, lastCheckDate: this.verifyLastCheckDate },
        width: '480px',
        maxWidth: '100vw',
        panelClass: ['custom-dialog', 'verify-dialog-responsive']
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.verifyContextMenuVisible) return;
    const target = event.target as Element;
    if (!target.closest('.verify-context-menu')) {
      this.closeVerifyContextMenu();
    }
  }

  /**
   * Descarga el documento firmado completo
   */
  downloadSignedFile(): void {
    if (!this.verifyFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: FILE_ERROR_MESSAGES.DOWNLOAD_NO_FILE,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    // Leer el archivo como blob y descargarlo
    const reader = new FileReader();
    reader.onload = () => {
      const blob = new Blob([reader.result as ArrayBuffer], { type: this.verifyFile!.type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.verifyFile!.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.notificationService.showDownloadSuccessToast('Documento descargado');
    };
    reader.onerror = () => {
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'error',
        title: 'Error',
        text: FILE_ERROR_MESSAGES.DOWNLOAD_ERROR,
        confirmButtonText: 'Entendido'
      });
    };
    reader.readAsArrayBuffer(this.verifyFile);
  }

  downloadOriginalFile(): void {
    if (!this.verifyFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Advertencia',
        text: FILE_ERROR_MESSAGES.DOWNLOAD_NO_FILE,
        confirmButtonText: 'Entendido',
        ...this.notificationService.getSwalBaseConfig()
      });
      return;
    }

    this.uiHelper.showSpinner('Extrayendo contenido original...', 'download');
    this.verifyService.downloadOriginalFile(this.verifyFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: (res) => {
        this.uiHelper.hideSpinner();
        const blob = new Blob([res.body!], { type: 'application/octet-stream' });

        const contentDisposition = res.headers.get('Content-Disposition');
        let filename = 'contenido_original';

        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, '').trim();
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.notificationService.showDownloadSuccessToast('Contenido original descargado');
      },
      error: (err) => {
        this.uiHelper.hideSpinner();
        console.error(err);
        const detail = err instanceof Error && err.message
          ? err.message
          : FILE_ERROR_MESSAGES.DOWNLOAD_ERROR;
        Swal.fire({
          ...this.notificationService.getSwalBaseConfig(),
          icon: 'error',
          title: 'No se pudo ver el contenido',
          text: detail,
          confirmButtonText: 'Entendido'
        });
      }
    });
  }

  /**
   * Tras verificar: llevar el archivo a la sección de firmar con estado y URL coherentes.
   * Navega a /firmar y rellena SignStateService (evita spinner infinito al firmar).
   */
  signAgain(): void {
    const file = this.verifyFile;
    if (!file) return;

    const name = file.name.toLowerCase();
    const fileType: FileType = name.endsWith(FILE_EXTENSIONS.PDF) ? 'pdf' : 'other';

    const applyFileToSignState = (): void => {
      this.resetVisibleSignatureState();
      this.signStateService.setFileType(fileType);
      this.signStateService.setFiles([file]);
      this.convertFilesToBase64([file], 0);
      Swal.fire({
        ...this.notificationService.getSwalBaseConfig(),
        icon: 'info',
        title: 'Archivo cargado',
        text: 'El documento está listo para firmar. Puedes continuar con el proceso de firma digital.',
        confirmButtonText: 'Entendido',
        customClass: {
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          confirmButton: 'custom-swal-button'
        }
      });
    };

    this.router.navigate(['/firmar']).then((navigated) => {
      if (!navigated) {
        this.tabStateService.setActiveTab('sign');
      }
      // Tras NavigationEnd + clearForms (verify→sign), poblar firma en el siguiente macrotask
      setTimeout(() => applyFileToSignState(), 0);
    });
  }


  // --- Utilities ---
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  downloadSignedFileFromSign(index: number): void {
    const src = this.signedFileSrcs[index];
    let originalName = 'SignedDocument';

    if (this.signFileType === 'pdf') {
      originalName = this.signFiles[index]?.name || originalName;
    } else if (this.signFileType === 'other') {
      originalName = this.signFiles[index]?.name || originalName;
      if (!originalName.toLowerCase().endsWith(FILE_EXTENSIONS.P7Z)) {
        originalName += FILE_EXTENSIONS.P7Z;
      }
    }

    const downloadName = `Signed_${originalName}`;
    const link = document.createElement('a');
    link.href = src;
    link.download = downloadName;
    link.click();
  }

  downloadAllSignedFiles(): void {
    this.signedFileSrcs.forEach((src, index) => {
      let name = this.signFiles[index]?.name || `SignedDocument${index}`;
      if (this.signFileType === 'other') {
        if (!name.toLowerCase().endsWith(FILE_EXTENSIONS.P7Z)) {
          name += FILE_EXTENSIONS.P7Z;
        }
      }
      const downloadName = `Signed_${name}`;
      const link = document.createElement('a');
      link.href = src;
      link.download = downloadName;
      link.click();
    });
  }

  viewSignedPdf(index: number): void {
    const state = this.signStateService.currentState;
    if (state.fileType === 'pdf') {
      this.signStateService.setShowPdfViewer(true, index);
    }
  }

  closePdfViewer(): void {
    this.signStateService.setShowPdfViewer(false);
  }

  openSignedFilesModal(fileType: 'pdf' | 'other', originalFileNames?: string[]): void {
    const state = this.signStateService.currentState;
    const modalData: SignedFilesModalData = {
      signedFileSrcs: state.signedFiles,
      originalFileNames: originalFileNames?.length
        ? originalFileNames
        : state.files.map(file => file.name),
      fileType: fileType,
      remitterName: state.selectedCertificate?.cn || undefined,
      serial: this.auditSerial || state.selectedCertificate?.serial || undefined,
      transactionId: this.auditTransactionId || undefined,
      identificationNumber: this.auditIdentificationNumber || state.user || undefined
    };

    const dialogRef = this.dialog.open(SignedFilesModalComponent, {
      width: 'calc(100vw - 32px)',
      maxWidth: 'none',
      maxHeight: '90vh',
      panelClass: ['custom-dialog', 'signed-files-dialog'],
      enterAnimationDuration: '420ms',
      exitAnimationDuration: '280ms',
      data: modalData,
      disableClose: true
    });

    // Limpiar opciones de firma visible al completar la firma
    this.resetVisibleSignatureState();

    // Ocultar la sección de documentos firmados cuando se abre el modal
    this.signStateService.setSigned(false);

    // Restaurar cuando se cierra el modal
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signStateService.setSigned(true);
      });
  }

  /**
   * Limpia todas las suscripciones al destruir el componente
   * Previene memory leaks
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

